import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthProvider, useAuth } from "./AuthContext";
import { useLanguage } from "./i18n";
import LoginPage from "./LoginPage";
import BishopDashboard from "./BishopDashboard";
import WeeklyScheduleComponent from "./WeeklyScheduleComponent";
import ContactForm from "./ContactForm";
import UserProfile from "./UserProfile";
import ChurchUserManagement from "./ChurchUserManagement";
import UserManagement from "./UserManagement";
import { toCopticDate, formatDateForAPI, formatDateForDisplay } from "./utils";
import { Calendar } from "./components/ui/calendar";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Badge } from "./components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./components/ui/accordion.jsx";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "./components/ui/alert-dialog";
import { Trash2, Plus, BarChart3, Calendar as CalendarIcon, Users, BookOpen, Edit, Upload, FileSpreadsheet, Download, Database, AlertTriangle, Settings, AlertCircle, LogOut, UserPlus, Printer, Globe, Phone, MessageCircle, CheckSquare, FileText, RefreshCw, ChevronUp, ChevronDown, ArrowRight } from "lucide-react";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";

// Fix ResizeObserver loop error
const errorHandler = (event) => {
  if (event.error?.message?.includes('ResizeObserver loop')) {
    console.warn('ResizeObserver loop completed - ignoring');
    event.preventDefault();
  }
};

window.addEventListener('error', errorHandler);

// Component to suppress ResizeObserver errors in console
const origError = console.error;
console.error = function (...args) {
  if (args.length > 0 && typeof args[0] === 'string' && args[0].includes('ResizeObserver loop')) {
    return;
  }
  origError.apply(console, args);
};

// Dynamic backend URL detection
// For localhost: always use localhost:8001
// For production/preview: use current domain or REACT_APP_BACKEND_URL
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL 
  || (window.location.origin.includes('localhost') 
    ? 'http://localhost:8001'
    : window.location.origin);
const API = `${BACKEND_URL}/api`;

console.log('Backend URL:', BACKEND_URL);

// Main Dashboard Component
const Dashboard = () => {
  const { isAdmin: checkIsAdmin, isSuperAdmin: checkIsSuperAdmin, user, logout, token } = useAuth();
  const { t, language, toggleLanguage, isRTL } = useLanguage();
  const isAdmin = () => checkIsAdmin();
  const isSuperAdmin = () => checkIsSuperAdmin();
  
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [deacons, setDeacons] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [readingTypes, setReadingTypes] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [newDeaconName, setNewDeaconName] = useState("");
  const [newDeaconPhone, setNewDeaconPhone] = useState("");
  const [newDeaconAddress, setNewDeaconAddress] = useState("");
  const [isAddDeaconOpen, setIsAddDeaconOpen] = useState(false);
  const [statistics, setStatistics] = useState([]);
  const [readingTypeStats, setReadingTypeStats] = useState([]);
  const [inactiveDeacons, setInactiveDeacons] = useState([]);
  const [readingTypeStatistics, setReadingTypeStatistics] = useState([]);
  const [saintOfDay, setSaintOfDay] = useState(null);
  const [newServiceType, setNewServiceType] = useState({ name: "", description: "" });
  const [isServiceTypeDialogOpen, setIsServiceTypeDialogOpen] = useState(false);
  const [editingServiceType, setEditingServiceType] = useState(null);
  const [isEditServiceTypeDialogOpen, setIsEditServiceTypeDialogOpen] = useState(false);
  const [isDeleteServiceTypeDialogOpen, setIsDeleteServiceTypeDialogOpen] = useState(false);
  const [serviceTypeToDelete, setServiceTypeToDelete] = useState(null);
  const [passwordDialogAction, setPasswordDialogAction] = useState(null);
  const [passwordDialogTitle, setPasswordDialogTitle] = useState("");
  const [passwordDialogMessage, setPasswordDialogMessage] = useState("");
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importType, setImportType] = useState("assignments"); // "assignments" or "deacons"
  const [detailedAssignments, setDetailedAssignments] = useState([]);
  const [selectedAssignments, setSelectedAssignments] = useState(new Set());
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [editingDeacon, setEditingDeacon] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isEditDeaconDialogOpen, setIsEditDeaconDialogOpen] = useState(false);
  const [adminPassword, setAdminPassword] = useState("");
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [isChangePasswordDialogOpen, setIsChangePasswordDialogOpen] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showOfflineMessage, setShowOfflineMessage] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [lastDataLoad, setLastDataLoad] = useState(null);
  const [networkStatus, setNetworkStatus] = useState(navigator.onLine ? 'online' : 'offline');
  const [appHealth, setAppHealth] = useState('unknown');
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [selectedReadingType, setSelectedReadingType] = useState("");
  const [selectedDeaconId, setSelectedDeaconId] = useState("");
  const [deaconSearch, setDeaconSearch] = useState("");
  const [ranks, setRanks] = useState([]);
  const [activeView, setActiveView] = useState(null); // null = show menu, string = show content
  const [isRanksDialogOpen, setIsRanksDialogOpen] = useState(false);
  const [editableRanks, setEditableRanks] = useState([]);
  const [selectedDeaconStats, setSelectedDeaconStats] = useState(null);
  const [isReadingDetailsDialogOpen, setIsReadingDetailsDialogOpen] = useState(false);
  const [deaconSortBy, setDeaconSortBy] = useState("name");
  const [deaconProfileOpen, setDeaconProfileOpen] = useState(false);
  const [deaconProfileData, setDeaconProfileData] = useState(null);
  const [deaconProfileLoading, setDeaconProfileLoading] = useState(false);
  const [statsSortBy, setStatsSortBy] = useState("name");

  // Bulk Assignment States
  const [bulkAssignmentDate, setBulkAssignmentDate] = useState(new Date());
  const [bulkReadingType, setBulkReadingType] = useState("");
  const [selectedDeacons, setSelectedDeacons] = useState(new Set());
  const [bulkDeaconSearch, setBulkDeaconSearch] = useState("");
  const [isSubmittingBulk, setIsSubmittingBulk] = useState(false);
  const [scheduleRefreshKey, setScheduleRefreshKey] = useState(0);

  // Service Management States
  const [selectedServiceType, setSelectedServiceType] = useState("");
  const [serviceAssignments, setServiceAssignments] = useState([]);
  const [selectedServiceAssignments, setSelectedServiceAssignments] = useState(new Set());
  const [isLoadingServiceAssignments, setIsLoadingServiceAssignments] = useState(false);

  // Church Information State
  const [churchInfo, setChurchInfo] = useState(null);
  const [isChurchSettingsDialogOpen, setIsChurchSettingsDialogOpen] = useState(false);
  const [churchSettings, setChurchSettings] = useState({
    church_name: "",
    church_name_short: "",
    church_location: ""
  });

  // Get today's date info
  const today = new Date();
  const copticToday = toCopticDate(today);
  const formatTodayGregorian = formatDateForDisplay(today);

  // Get day name in Arabic
  const getDayName = (date) => {
    const days = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
    return days[date.getDay()];
  };

  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
      setIsMobile(mobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch church info on app start
  useEffect(() => {
    fetchChurchInfo();
  }, []);

  // Check for new deployments (show update banner once per day max)
  useEffect(() => {
    let buildTime = null;
    const DISMISS_KEY = 'update_banner_dismissed';
    const checkVersion = async () => {
      try {
        const dismissed = localStorage.getItem(DISMISS_KEY);
        if (dismissed) {
          const dismissedDate = new Date(dismissed).toDateString();
          if (dismissedDate === new Date().toDateString()) return;
        }
        const res = await axios.get(`${API}/version`);
        const serverBuild = res.data.build_time;
        if (buildTime === null) {
          buildTime = serverBuild;
        } else if (serverBuild !== buildTime) {
          setUpdateAvailable(true);
        }
      } catch (e) { /* ignore */ }
    };
    checkVersion();
    const interval = setInterval(checkVersion, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Network status monitoring
    const updateNetworkStatus = () => {
      setNetworkStatus(navigator.onLine ? 'online' : 'offline');
    };

    window.addEventListener('online', updateNetworkStatus);
    window.addEventListener('offline', updateNetworkStatus);

    // Periodic health check
    const healthCheckInterval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/deacons`, { 
          timeout: 5000,
          headers: { 
            'Cache-Control': 'no-cache',
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.status === 200) {
          setAppHealth('healthy');
        } else {
          setAppHealth('degraded');
        }
      } catch (error) {
        console.warn('Health check failed:', error.message);
        setAppHealth('unhealthy');
        
        // If health check fails multiple times, show warning
        if (appHealth === 'unhealthy') {
          toast.warning(t('weakConnection'), {
            duration: 8000,
            id: 'health-warning' // Prevent duplicate toasts
          });
        }
      }
    }, 30000); // Check every 30 seconds

    return () => {
      window.removeEventListener('online', updateNetworkStatus);
      window.removeEventListener('offline', updateNetworkStatus);
      clearInterval(healthCheckInterval);
    };
  }, [appHealth]);

  // Auto-refresh data when coming back online
  useEffect(() => {
    if (networkStatus === 'online' && appHealth === 'healthy') {
      console.log('🔄 Network restored, refreshing critical data...');
      // Refresh only critical data
      setTimeout(() => {
        fetchDeacons(0).catch(console.error);
        fetchReadingTypes(0).catch(console.error);
        fetchServiceTypes(0).catch(console.error);
        fetchRanks().catch(console.error);
      }, 1000);
    }
  }, [networkStatus, appHealth]);
  useEffect(() => {
    axios.defaults.timeout = 15000; // Increase timeout to 15 seconds
    axios.defaults.headers.common['Cache-Control'] = 'no-cache';
    
    // Add axios interceptor for better error handling
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('API Error:', error);
        if (error.code === 'ECONNABORTED') {
          console.log('Request timeout - server might be sleeping');
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  // Health check function to wake up the server
  const performHealthCheck = async () => {
    try {
      console.log("Performing health check...");
      await axios.get(`${API}/deacons`, { 
        timeout: 5000,
        ...getAuthHeaders()
      });
      console.log("✅ Server is awake and responsive");
      return true;
    } catch (error) {
      console.log("⚠️ Server is sleeping or slow, warming up...");
      return false;
    }
  };

  // Enhanced fetch function with health check
  const fetchWithHealthCheck = async (fetchFunction, maxAttempts = 4) => {
    let attempt = 1;
    
    while (attempt <= maxAttempts) {
      try {
        console.log(`Fetch attempt ${attempt}/${maxAttempts}`);
        
        if (attempt === 1) {
          // First attempt - quick health check
          const isHealthy = await performHealthCheck();
          if (!isHealthy && attempt < maxAttempts) {
            console.log("Server warming up, waiting before retry...");
            await new Promise(resolve => setTimeout(resolve, 3000));
            attempt++;
            continue;
          }
        }
        
        await fetchFunction();
        console.log("✅ Fetch successful");
        return;
        
      } catch (error) {
        console.error(`Attempt ${attempt} failed:`, error);
        
        if (attempt === maxAttempts) {
          console.error("All attempts failed");
          throw error;
        }
        
        // Progressive delay: 2s, 4s, 6s
        const delay = attempt * 2000;
        console.log(`Waiting ${delay}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        attempt++;
      }
    }
  };
  // Enhanced initialization with better error handling and fallbacks
  useEffect(() => {
    const initializeData = async () => {
      setIsLoadingData(true);
      
      try {
        console.log("🚀 Starting enhanced data initialization...");
        
        // Critical data loading with individual error handling
        const loadPromises = [
          { name: 'Deacons', fn: () => fetchWithHealthCheck(() => fetchDeacons(0)), critical: true },
          { name: 'Reading Types', fn: () => fetchWithHealthCheck(() => fetchReadingTypes(0)), critical: true },
          { name: 'Service Types', fn: () => fetchWithHealthCheck(() => fetchServiceTypes(0)), critical: true },
          { name: 'Statistics', fn: () => fetchWithHealthCheck(() => fetchStatistics(0)), critical: false },
          { name: 'Inactive Deacons', fn: () => fetchWithHealthCheck(() => fetchInactiveDeacons(0)), critical: false },
          { name: 'Reading Type Statistics', fn: () => fetchWithHealthCheck(() => fetchReadingTypeStatistics(0)), critical: false },
          { name: 'Saint of Day', fn: () => fetchSaintOfDay(), critical: false },
          { name: 'Ranks', fn: () => fetchRanks(), critical: false }
        ];
        
        const results = await Promise.allSettled(loadPromises.map(p => p.fn()));
        
        // Check results and handle failures
        let criticalFailed = false;
        let partialFailed = 0;
        
        results.forEach((result, index) => {
          const promise = loadPromises[index];
          if (result.status === 'rejected') {
            console.error(`❌ Failed to load ${promise.name}:`, result.reason);
            if (promise.critical) {
              criticalFailed = true;
            } else {
              partialFailed++;
            }
          } else {
            console.log(`✅ Successfully loaded ${promise.name}`);
          }
        });
        
        if (criticalFailed) {
          console.error("❌ Critical data loading failed");
          toast.error(t('errorLoadingData') + ' - ' + t('pleaseReloadPage'), {
            duration: 10000,
            action: {
              label: "إعادة محاولة",
              onClick: () => window.location.reload()
            }
          });
        } else if (partialFailed > 0) {
          console.warn(`⚠️ ${partialFailed} non-critical data sources failed to load`);
          toast.warning(t('loadingData'), {
            duration: 5000
          });
        } else {
          console.log("✅ All data loaded successfully");
          toast.success(t('savedSuccessfully'), { duration: 3000 });
        }
        
      } catch (error) {
        console.error("❌ Critical error during initialization:", error);
        toast.error(t('errorLoadingData') + ' - ' + t('pleaseReloadPage'), {
          duration: 15000,
          action: {
            label: "إعادة تحميل",
            onClick: () => window.location.reload()
          }
        });
      } finally {
        setIsLoadingData(false);
      }
    };

    // Add delay for first load to avoid race conditions
    const timer = setTimeout(() => {
      initializeData();
    }, 500);

    return () => clearTimeout(timer);

    // Keep service worker alive and refresh data periodically
    const keepAliveInterval = setInterval(() => {
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'KEEP_ALIVE'
        });
      }
    }, 30000); // Every 30 seconds

    // Aggressive data refresh interval
    const dataRefreshInterval = setInterval(() => {
      console.log("Periodic data refresh...");
      // Only refresh if tab is visible
      if (!document.hidden) {
        fetchDeacons();
        fetchReadingTypes();
        fetchServiceTypes();
      }
    }, 60000); // Every minute

    // Refresh data when app becomes visible again (wake up from background)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('App became visible, refreshing data...');
        fetchDeacons();
        fetchStatistics();
      fetchReadingTypeStatistics();
        fetchInactiveDeacons();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Focus event to catch when tab becomes active
    const handleFocus = () => {
      console.log('App gained focus, refreshing data...');
      fetchDeacons();
      fetchStatistics();
      fetchReadingTypeStatistics();
      fetchInactiveDeacons();
    };

    window.addEventListener('focus', handleFocus);

    // Online/offline event handlers
    const handleOnline = () => {
      console.log('App came back online, refreshing data...');
      setIsOnline(true);
      setShowOfflineMessage(false);
      fetchDeacons();
      fetchReadingTypes();
      fetchServiceTypes();
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowOfflineMessage(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup
    return () => {
      clearInterval(keepAliveInterval);
      clearInterval(dataRefreshInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Check assignments and saint of day whenever selectedDate changes
  useEffect(() => {
    if (selectedDate) {
      fetchAssignmentsForDate(selectedDate);
      fetchSaintOfDay(selectedDate);
    }
  }, [selectedDate]);

  // Helper to get auth headers - uses token from useAuth hook
  const getAuthHeaders = () => {
    return {
      headers: { Authorization: `Bearer ${token}` }
    };
  };

  const fetchDeacons = async (retryCount = 0) => {
    try {
      console.log("Fetching deacons...");
      const response = await axios.get(`${API}/deacons`, getAuthHeaders());
      setDeacons(response.data);
      setLastDataLoad(new Date().toLocaleTimeString());
      console.log("Deacons loaded successfully:", response.data.length);
    } catch (error) {
      console.error("Error fetching deacons:", error);
      // Only use built-in retry logic if called directly (not through fetchWithHealthCheck)
      if (retryCount < 3 && arguments.length > 0) {
        console.log(`Retrying fetchDeacons (${retryCount + 1}/3)...`);
        setTimeout(() => fetchDeacons(retryCount + 1), 2000);
      } else if (arguments.length > 0) {
        toast.error(t('errorLoadingDeacons') + ' - ' + t('pleaseReloadPage'));
      } else {
        throw error; // Re-throw for fetchWithHealthCheck to handle
      }
    }
  };
  const fetchRanks = async () => {
    try {
      const response = await axios.get(`${API}/ranks`, getAuthHeaders());
      setRanks(response.data);
    } catch (error) { console.error("Error fetching ranks:", error); }
  };

  const handleUpdateDeaconRank = async (deaconId, rank) => {
    try {
      await axios.put(`${API}/deacons/${deaconId}/rank`, { rank }, getAuthHeaders());
      setDeacons(prev => prev.map(d => d.id === deaconId ? {...d, rank} : d));
    } catch (error) {
      toast.error('خطأ في تحديث الرتبة');
    }
  };

  const handleSaveRanks = async () => {
    try {
      await axios.put(`${API}/ranks`, { ranks: editableRanks }, getAuthHeaders());
      setRanks(editableRanks);
      setIsRanksDialogOpen(false);
      toast.success('تم تحديث الرتب بنجاح');
    } catch (error) {
      toast.error('خطأ في حفظ الرتب');
    }
  };

  const openDeaconProfile = async (deacon) => {
    setDeaconProfileOpen(true);
    setDeaconProfileLoading(true);
    setDeaconProfileData(null);
    try {
      const res = await axios.get(`${API}/deacons/${deacon.id}/assignments`, getAuthHeaders());
      setDeaconProfileData(res.data);
    } catch (error) {
      toast.error('خطأ في تحميل البيانات');
    } finally {
      setDeaconProfileLoading(false);
    }
  };

  const getSortedDeacons = () => {
    if (!deacons || deacons.length === 0) return [];
    const sorted = [...deacons];
    switch (deaconSortBy) {
      case "name": sorted.sort((a, b) => (a.name || "").localeCompare(b.name || "", 'ar')); break;
      case "rank": sorted.sort((a, b) => (a.rank || "zzz").localeCompare(b.rank || "zzz", 'ar')); break;
      case "participation_asc": sorted.sort((a, b) => (a.total_participation || 0) - (b.total_participation || 0)); break;
      case "participation_desc": sorted.sort((a, b) => (b.total_participation || 0) - (a.total_participation || 0)); break;
      case "date_new": sorted.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")); break;
      case "date_old": sorted.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || "")); break;
      case "active": sorted.sort((a, b) => (b.schedule_active !== false ? 1 : 0) - (a.schedule_active !== false ? 1 : 0)); break;
      case "inactive": sorted.sort((a, b) => (a.schedule_active !== false ? 1 : 0) - (b.schedule_active !== false ? 1 : 0)); break;
      default: break;
    }
    return sorted;
  };

  const getSortedStats = () => {
    if (!statistics || statistics.length === 0) return [];
    const sorted = [...statistics];
    switch (statsSortBy) {
      case "name": sorted.sort((a, b) => (a.name || "").localeCompare(b.name || "", 'ar')); break;
      case "rank": sorted.sort((a, b) => (a.rank || "zzz").localeCompare(b.rank || "zzz", 'ar')); break;
      case "participation_asc": sorted.sort((a, b) => (a.total_readings || 0) - (b.total_readings || 0)); break;
      case "participation_desc": sorted.sort((a, b) => (b.total_readings || 0) - (a.total_readings || 0)); break;
      case "date_new": sorted.sort((a, b) => (b.last_reading_date || "").localeCompare(a.last_reading_date || "")); break;
      case "date_old": sorted.sort((a, b) => (a.last_reading_date || "1900").localeCompare(b.last_reading_date || "1900")); break;
      default: break;
    }
    return sorted;
  };




  const fetchReadingTypes = async (retryCount = 0) => {
    try {
      const response = await axios.get(`${API}/reading-types`, getAuthHeaders());
      setReadingTypes(response.data.reading_types);
      console.log("Reading types loaded successfully:", response.data.reading_types.length);
    } catch (error) {
      console.error("Error fetching reading types:", error);
      if (retryCount < 3) {
        console.log(`Retrying fetchReadingTypes (${retryCount + 1}/3)...`);
        setTimeout(() => fetchReadingTypes(retryCount + 1), 2000);
      } else {
        toast.error(t('errorLoadingData'));
      }
    }
  };

  const fetchServiceTypes = async (retryCount = 0) => {
    try {
      const response = await axios.get(`${API}/service-types`, getAuthHeaders());
      // البيانات تأتي في service_types وليس مباشرة
      setServiceTypes(response.data.service_types || response.data);
      console.log("Service types loaded successfully:", (response.data.service_types || response.data).length);
    } catch (error) {
      console.error("Error fetching service types:", error);
      if (retryCount < 3) {
        console.log(`Retrying fetchServiceTypes (${retryCount + 1}/3)...`);
        setTimeout(() => fetchServiceTypes(retryCount + 1), 2000);
      } else {
        toast.error(t('errorLoadingData'));
      }
    }
  };

  const fetchAssignmentsForDate = async (date, retryCount = 0) => {
    try {
      const formattedDate = formatDateForAPI(date);
      if (!formattedDate) return;

      const response = await axios.get(`${API}/assignments/by-date/${formattedDate}`, getAuthHeaders());
      setAssignments(response.data.assignments || {});
      
      // جلب التفاصيل أيضاً - البيانات تأتي في assignments وليس مباشرة
      const detailedResponse = await axios.get(`${API}/assignments-detailed/${formattedDate}`, getAuthHeaders());
      setDetailedAssignments(detailedResponse.data.assignments || []);
      console.log("Assignments loaded for", formattedDate, ":", Object.keys(response.data.assignments || {}).length, "reading types");
      console.log("Detailed assignments:", (detailedResponse.data.assignments || []).length, "assignments");
    } catch (error) {
      console.error("Error fetching assignments:", error);
      if (retryCount < 3) {
        console.log(`Retrying fetchAssignmentsForDate (${retryCount + 1}/3)...`);
        setTimeout(() => fetchAssignmentsForDate(date, retryCount + 1), 2000);
      } else {
        toast.error(t('errorLoadingAssignments'));
      }
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await axios.get(`${API}/statistics`, getAuthHeaders());
      // البيانات تأتي في statistics وليس مباشرة
      setStatistics(response.data.statistics || response.data);
      console.log("Statistics loaded successfully:", (response.data.statistics || response.data).length, "deacons");
    } catch (error) {
      console.error("Error fetching statistics:", error);
      toast.error(t('errorLoadingStatistics'));
    }
  };

  const fetchInactiveDeacons = async () => {
    try {
      console.log("Fetching inactive deacons...");
      const response = await axios.get(`${API}/statistics/inactive-deacons`, getAuthHeaders());
      console.log("Inactive deacons response:", response.data);
      setInactiveDeacons(response.data.inactive_deacons || []);
      console.log("Inactive deacons state set:", (response.data.inactive_deacons || []).length, "deacons");
    } catch (error) {
      console.error("Error fetching inactive deacons:", error);
      toast.error(t('errorLoadingData'));
    }
  };

  const fetchReadingTypeStatistics = async (showError = true) => {
    try {
      console.log("Fetching reading type statistics...");
      const response = await axios.get(`${API}/statistics/by-reading-type`, getAuthHeaders());
      console.log("Reading type statistics response:", response.data);
      
      // API returns data directly, not nested in reading_type_statistics
      const statistics = response.data.reading_type_statistics || response.data || [];
      setReadingTypeStatistics(statistics);
      console.log("Reading type statistics loaded:", statistics.length, "types");
    } catch (error) {
      console.error("Error fetching reading type statistics:", error);
      if (showError) {
        toast.error(t('errorLoadingData'));
      }
      setReadingTypeStatistics([]); // Set empty array on error
    }
  };

  const fetchSaintOfDay = async (date = null) => {
    try {
      // Format date as YYYY-MM-DD in local timezone (not UTC)
      let dateParam = '';
      if (date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        dateParam = `?date=${year}-${month}-${day}`;
      }
      const response = await axios.get(`${API}/saint-of-day${dateParam}`, getAuthHeaders());
      setSaintOfDay(response.data);
      console.log("Saint of day loaded:", response.data);
    } catch (error) {
      console.error("Error fetching saint of day:", error);
      // Don't show error toast - this is not critical
    }
  };

  // Fetch Church Information
  const fetchChurchInfo = async () => {
    try {
      console.log("Fetching church information...");
      const response = await axios.get(`${API}/church-info`, getAuthHeaders());
      console.log("Church info response:", response.data);
      setChurchInfo(response.data);
      
      // Set document title with church name
      if (response.data.church_name_short) {
        document.title = `${response.data.church_name_short} - إدارة الشمامسة`;
      }
    } catch (error) {
      console.error("Error fetching church info:", error);
      // Set default church info
      setChurchInfo({
        church_name: "نظام إدارة الشمامسة",
        church_name_short: "إدارة الشمامسة",
        church_location: ""
      });
    }
  };

  const openChurchSettingsDialog = () => {
    // Pre-fill form with current church info
    if (churchInfo) {
      setChurchSettings({
        church_name: churchInfo.church_name || "",
        church_name_short: churchInfo.church_name_short || "",
        church_location: churchInfo.church_location || ""
      });
    }
    setIsChurchSettingsDialogOpen(true);
  };

  const handleUpdateChurchSettings = async (password) => {
    try {
      const response = await axios.post(`${API}/church-settings`, {
        church_name: churchSettings.church_name,
        church_name_short: churchSettings.church_name_short,
        church_location: churchSettings.church_location,
        password: password
      }, getAuthHeaders());

      toast.success(t('churchSettingsUpdated'));
      setIsChurchSettingsDialogOpen(false);
      
      // Refresh church info
      await fetchChurchInfo();
      
    } catch (error) {
      console.error("Error updating church settings:", error);
      if (error.response && error.response.data.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error(t('errorOccurred'));
      }
    }
  };

  const handleAddDeacon = async () => {
    if (!newDeaconName.trim()) {
      toast.error(t('pleaseEnterDeaconName'));
      return;
    }

    try {
      await axios.post(`${API}/deacons`, { 
        name: newDeaconName.trim(),
        phone: newDeaconPhone.trim() || null,
        address: newDeaconAddress.trim() || null
      }, getAuthHeaders());
      toast.success(t('deaconAddedSuccessfully'));
      
      setNewDeaconName("");
      setNewDeaconPhone("");
      setNewDeaconAddress("");
      setIsAddDeaconOpen(false);
      fetchDeacons();
      fetchStatistics();
      fetchReadingTypeStatistics();
    } catch (error) {
      console.error("Error adding deacon:", error);
      toast.error(t('errorAddingDeacon'));
    }
  };

  const handleAddAssignment = async () => {
    if (!selectedDeaconId || !selectedReadingType) {
      toast.error(t('pleaseSelectDeaconAndService'));
      return;
    }

    try {
      const assignmentData = {
        date: formatDateForAPI(selectedDate),
        deacon_id: selectedDeaconId,
        reading_type: selectedReadingType
      };

      await axios.post(`${API}/assignments`, assignmentData, getAuthHeaders());
      toast.success(t('assignmentAddedSuccessfully'));
      
      // Reset form and refresh data
      setSelectedDeaconId("");
      setSelectedReadingType("");
      fetchAssignmentsForDate(selectedDate);
      fetchStatistics();
      fetchReadingTypeStatistics(); // Refresh statistics after adding assignment
      fetchInactiveDeacons(); // Refresh inactive deacons list
    } catch (error) {
      console.error("Error adding assignment:", error);
      
      // Check if it's a duplicate assignment error
      if (error.response && error.response.status === 400 && error.response.data.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error(t('errorAddingAssignment'));
      }
    }
  };

  const executeWithPassword = (action, title, message) => {
    setPasswordDialogAction(() => async (password) => {
      try {
        await action(password);
        setIsPasswordDialogOpen(false);
        setAdminPassword("");
        setPendingAction(null);
      } catch (error) {
        console.error("Action failed:", error);
        if (error.response?.status === 401) {
          toast.error(t('wrongPassword'));
        } else if (error.response?.status === 404) {
          toast.error(t('notFound'));
        } else if (error.response?.status === 422) {
          toast.error(t('errorOccurred'));
        } else {
          toast.error(t('errorOccurred'));
        }
      }
    });
    setPasswordDialogTitle(title);
    setPasswordDialogMessage(message);
    setIsPasswordDialogOpen(true);
  };

  const handlePasswordSubmit = async () => {
    if (!passwordDialogAction) return;
    
    try {
      await passwordDialogAction(adminPassword);
    } catch (error) {
      // Error already handled in executeWithPassword
    }
  };

  const handleDeleteDeacon = async (deaconId, password) => {
    try {
      await axios.delete(`${API}/deacons/${deaconId}`, {
        data: { password },
        ...getAuthHeaders()
      });
      toast.success(t('deaconDeletedSuccessfully'));
      fetchDeacons();
      fetchStatistics();
      fetchReadingTypeStatistics();
    } catch (error) {
      throw error;
    }
  };

  const handleEditDeacon = async (deaconId, newName, newPhone, newAddress, password) => {
    try {
      console.log("Editing deacon:", deaconId, "to:", newName, newPhone, newAddress);
      await axios.put(`${API}/deacons/${deaconId}`, {
        name: newName,
        phone: newPhone || null,
        address: newAddress || null,
        password: password
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      toast.success(t('deaconEditedSuccessfully'));
      setIsEditDeaconDialogOpen(false);
      setEditingDeacon(null);
      fetchDeacons();
      fetchStatistics();
      fetchReadingTypeStatistics();
    } catch (error) {
      console.error("Error editing deacon:", error);
      if (error.response?.status === 401) {
        toast.error(t('wrongPassword'));
      } else if (error.response?.status === 404) {
        toast.error(t('notFound'));
      } else {
        toast.error(t('errorEditingDeacon'));
      }
      throw error;
    }
  };

  const handleDeleteAssignment = async (assignmentId, password) => {
    try {
      console.log("Deleting assignment:", assignmentId, "with password provided");
      await axios.delete(`${API}/assignments/${assignmentId}`, {
        data: { password: password || "" },
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      toast.success(t('assignmentDeletedSuccessfully'));
      fetchAssignmentsForDate(selectedDate);
      fetchStatistics();
      fetchReadingTypeStatistics();
      // Also update deacon profile if open
      if (deaconProfileData) {
        setDeaconProfileData(prev => prev ? ({
          ...prev,
          assignments: prev.assignments.filter(a => a.id !== assignmentId)
        }) : null);
      }
    } catch (error) {
      console.error("Error deleting assignment:", error);
      if (error.response?.status === 401) {
        toast.error(t('wrongPassword'));
      } else if (error.response?.status === 404) {
        toast.error(t('notFound'));
      } else {
        toast.error(t('errorDeletingAssignment'));
      }
      throw error;
    }
  };

  const handleBulkDeleteAssignments = async (password) => {
    try {
      const assignmentIds = Array.from(selectedAssignments);
      console.log("Bulk deleting assignments:", assignmentIds);
      
      let successCount = 0;
      let failCount = 0;
      
      for (const assignmentId of assignmentIds) {
        try {
          await axios.delete(`${API}/assignments/${assignmentId}`, {
            data: { password },
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });
          successCount++;
        } catch (error) {
          console.error(`Failed to delete assignment ${assignmentId}:`, error);
          failCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(t('deletedSuccessfully'));
      }
      if (failCount > 0) {
        toast.error(t('errorOccurred'));
      }
      
      // Clear selection and refresh
      setSelectedAssignments(new Set());
      fetchAssignmentsForDate(selectedDate);
      fetchStatistics();
      fetchReadingTypeStatistics();
    } catch (error) {
      console.error("Error in bulk delete:", error);
      toast.error(t('errorOccurred'));
      throw error;
    }
  };

  const toggleAssignmentSelection = (assignmentId) => {
    setSelectedAssignments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(assignmentId)) {
        newSet.delete(assignmentId);
      } else {
        newSet.add(assignmentId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedAssignments.size === detailedAssignments.length) {
      setSelectedAssignments(new Set());
    } else {
      setSelectedAssignments(new Set(detailedAssignments.map(a => a.id)));
    }
  };

  const fetchAssignmentsByServiceType = async (serviceType) => {
    if (!serviceType) {
      setServiceAssignments([]);
      return;
    }
    
    setIsLoadingServiceAssignments(true);
    try {
      // We'll fetch all assignments and filter by reading type on frontend
      // This is not ideal but works with current backend
      const response = await axios.get(`${API}/assignments-detailed/all`, getAuthHeaders());
      const allAssignments = response.data.assignments || [];
      
      // Filter by selected service type
      const filtered = allAssignments.filter(a => a.reading_type === serviceType);
      
      // Sort by date (newest first)
      filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
      
      setServiceAssignments(filtered);
      setSelectedServiceAssignments(new Set());
    } catch (error) {
      console.error("Error fetching service assignments:", error);
      toast.error(t('errorLoadingData'));
      setServiceAssignments([]);
    } finally {
      setIsLoadingServiceAssignments(false);
    }
  };

  const toggleServiceAssignmentSelection = (assignmentId) => {
    setSelectedServiceAssignments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(assignmentId)) {
        newSet.delete(assignmentId);
      } else {
        newSet.add(assignmentId);
      }
      return newSet;
    });
  };

  const toggleSelectAllServiceAssignments = () => {
    if (selectedServiceAssignments.size === serviceAssignments.length) {
      setSelectedServiceAssignments(new Set());
    } else {
      setSelectedServiceAssignments(new Set(serviceAssignments.map(a => a.id)));
    }
  };

  const handleBulkDeleteServiceAssignments = async (password) => {
    try {
      const assignmentIds = Array.from(selectedServiceAssignments);
      console.log("Bulk deleting service assignments:", assignmentIds);
      
      let successCount = 0;
      let failCount = 0;
      
      for (const assignmentId of assignmentIds) {
        try {
          await axios.delete(`${API}/assignments/${assignmentId}`, {
            data: { password },
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });
          successCount++;
        } catch (error) {
          console.error(`Failed to delete assignment ${assignmentId}:`, error);
          failCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(t('deletedSuccessfully'));
      }
      if (failCount > 0) {
        toast.error(t('errorOccurred'));
      }
      
      // Refresh the list
      fetchAssignmentsByServiceType(selectedServiceType);
      fetchStatistics();
      fetchReadingTypeStatistics();
    } catch (error) {
      console.error("Error in bulk delete:", error);
      toast.error(t('errorOccurred'));
      throw error;
    }
  };

  const handleEditAssignment = async (assignmentId, newDeaconId, password) => {
    try {
      console.log("Editing assignment:", assignmentId, "to deacon:", newDeaconId);
      await axios.put(`${API}/assignments/${assignmentId}`, {
        deacon_id: newDeaconId,
        password: password
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      toast.success(t('assignmentEditedSuccessfully'));
      setIsEditDialogOpen(false);
      setEditingAssignment(null);
      fetchAssignmentsForDate(selectedDate);
      fetchStatistics();
      fetchReadingTypeStatistics();
    } catch (error) {
      console.error("Error editing assignment:", error);
      if (error.response?.status === 401) {
        toast.error(t('wrongPassword'));
      } else if (error.response?.status === 404) {
        toast.error(t('notFound'));
      } else {
        toast.error(t('errorEditingAssignment'));
      }
      throw error;
    }
  };

  // Print assignments function
  const handlePrintAssignments = (printType = 'day') => {
    let printData = [];
    let title = '';
    let dateRange = '';
    
    if (printType === 'day') {
      // Print single day
      printData = detailedAssignments || [];
      title = t('weeklySchedule');
      dateRange = selectedDate ? selectedDate.toLocaleDateString('ar-EG', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }) : '';
    } else if (printType === 'week') {
      // Print week - we'll fetch the data for the week
      title = t('weeklySchedule');
      const startOfWeek = new Date(selectedDate);
      startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(endOfWeek.getDate() + 6);
      dateRange = `${startOfWeek.toLocaleDateString('ar-EG')} - ${endOfWeek.toLocaleDateString('ar-EG')}`;
    }
    
    // Create print window
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html dir="rtl" lang="ar">
      <head>
        <meta charset="UTF-8">
        <title>${title}</title>
        <style>
          @page {
            size: A4;
            margin: 1.5cm;
          }
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: 'Segoe UI', Tahoma, Arial, sans-serif;
            direction: rtl;
            background: white;
            color: #333;
            line-height: 1.6;
          }
          .header {
            text-align: center;
            padding: 20px 0;
            border-bottom: 3px solid #d97706;
            margin-bottom: 20px;
          }
          .header h1 {
            font-size: 24px;
            color: #92400e;
            margin-bottom: 5px;
          }
          .header .church-name {
            font-size: 18px;
            color: #b45309;
            margin-bottom: 10px;
          }
          .header .date {
            font-size: 16px;
            color: #78716c;
          }
          .content {
            padding: 0 20px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
          }
          th, td {
            border: 1px solid #d6d3d1;
            padding: 12px 15px;
            text-align: right;
          }
          th {
            background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
            color: #92400e;
            font-weight: bold;
            font-size: 14px;
          }
          td {
            font-size: 13px;
          }
          tr:nth-child(even) {
            background-color: #fefce8;
          }
          tr:hover {
            background-color: #fef9c3;
          }
          .reading-type {
            font-weight: bold;
            color: #b45309;
          }
          .deacon-name {
            color: #15803d;
          }
          .footer {
            margin-top: 30px;
            padding-top: 15px;
            border-top: 1px solid #e7e5e4;
            text-align: center;
            font-size: 11px;
            color: #78716c;
          }
          .no-data {
            text-align: center;
            padding: 40px;
            color: #78716c;
            font-size: 16px;
          }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>⛪ ${title}</h1>
          <div class="church-name">${churchInfo?.name || 'Diakonenverwaltung'}</div>
          <div class="date">${dateRange}</div>
        </div>
        <div class="content">
          ${printData.length > 0 ? `
            <table>
              <thead>
                <tr>
                  <th style="width: 10%;">#</th>
                  <th style="width: 45%;">نوع القراءة / الخدمة</th>
                  <th style="width: 45%;">اسم الشماس</th>
                </tr>
              </thead>
              <tbody>
                ${printData.map((assignment, index) => `
                  <tr>
                    <td style="text-align: center;">${index + 1}</td>
                    <td class="reading-type">${assignment.reading_type}</td>
                    <td class="deacon-name">${assignment.deacon_name}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          ` : `
            <div class="no-data">لا توجد توزيعات لهذا التاريخ</div>
          `}
        </div>
        <div class="footer">
          طُبع بتاريخ: ${new Date().toLocaleDateString('ar-EG', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </div>
        <script>
          window.onload = function() {
            window.print();
          }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleExportData = async () => {
    try {
      const response = await axios.get(`${API}/export`, {
        responseType: 'blob',
        ...getAuthHeaders()
      });
      
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `church_readings_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      
      window.URL.revokeObjectURL(url);
      toast.success(t('dataExportedSuccessfully'));
    } catch (error) {
      console.error("Error exporting data:", error);
      toast.error(t('errorExporting'));
    }
  };

  const handleImportData = async (file) => {
    if (!file) {
      toast.error(t('errorOccurred'));
      return;
    }

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error(t('errorOccurred'));
      return;
    }

    setIsImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/import-excel`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      toast.success(t('dataImportedSuccessfully'));
      setIsImportDialogOpen(false);
      
      // Refresh data
      fetchDeacons();
      fetchStatistics();
      fetchReadingTypeStatistics();
      fetchAssignmentsForDate(selectedDate);
    } catch (error) {
      console.error("Error importing data:", error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error(t('errorImporting'));
      }
    } finally {
      setIsImporting(false);
    }
  };

  // Import deacons only (for new churches)
  const handleImportDeacons = async (file) => {
    if (!file) {
      toast.error(t('errorOccurred'));
      return;
    }

    setIsImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/import-deacons`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      toast.success(t('dataImportedSuccessfully'));
      setIsImportDialogOpen(false);
      
      // Refresh deacons list
      fetchDeacons();
    } catch (error) {
      console.error("Error importing deacons:", error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error(t('errorImporting'));
      }
    } finally {
      setIsImporting(false);
    }
  };

  const handleClearAllData = async (password) => {
    try {
      await axios.post(`${API}/backup/clear-all`, { password }, getAuthHeaders());
      toast.success(t('allDataCleared'));
      
      // Reset all state
      setDeacons([]);
      setAssignments({});
      setDetailedAssignments([]);
      setStatistics([]);
      setInactiveDeacons([]);
      
      // Refresh data
      fetchDeacons();
      fetchStatistics();
      fetchReadingTypeStatistics();
      fetchAssignmentsForDate(selectedDate);
    } catch (error) {
      throw error;
    }
  };

  const handleChangePassword = async () => {
    if (!oldPassword || !newPassword) {
      toast.error(t('pleaseEnterDeaconName'));
      return;
    }

    if (newPassword.length < 5) {
      toast.error(t('passwordTooShort'));
      return;
    }

    try {
      await axios.post(`${API}/change-password`, {
        old_password: oldPassword,
        new_password: newPassword
      }, getAuthHeaders());

      toast.success(t('passwordChangedSuccessfully'));
      setIsChangePasswordDialogOpen(false);
      setOldPassword("");
      setNewPassword("");
    } catch (error) {
      console.error("Error changing password:", error);
      if (error.response?.status === 401) {
        toast.error(t('wrongPassword'));
      } else {
        toast.error(t('errorChangingPassword'));
      }
    }
  };

  const handleAddServiceType = async (password) => {
    if (!newServiceType.name.trim()) {
      toast.error(t('pleaseEnterDeaconName'));
      return;
    }

    try {
      await axios.post(`${API}/service-types?password=${password}`, {
        name: newServiceType.name.trim(),
        description: newServiceType.description.trim()
      }, getAuthHeaders());
      
      toast.success(t('serviceTypeAddedSuccessfully'));
      setNewServiceType({ name: "", description: "" });
      setIsServiceTypeDialogOpen(false);
      fetchServiceTypes();
      fetchReadingTypes();
      // Auto-refresh reading type statistics to show new service type
      fetchReadingTypeStatistics();
    } catch (error) {
      throw error;
    }
  };

  const handleEditServiceType = async (password) => {
    if (!editingServiceType?.name?.trim()) {
      toast.error(t('pleaseEnterDeaconName'));
      return;
    }

    try {
      await axios.put(`${API}/service-types/${editingServiceType.id}?password=${password}`, {
        name: editingServiceType.name.trim(),
        description: editingServiceType.description?.trim() || ""
      }, getAuthHeaders());
      
      toast.success(t('serviceTypeEditedSuccessfully'));
      setIsEditServiceTypeDialogOpen(false);
      setEditingServiceType(null);
      fetchServiceTypes();
      fetchReadingTypes();
      // Auto-refresh reading type statistics to reflect changes
      fetchReadingTypeStatistics();
    } catch (error) {
      throw error;
    }
  };

  const handleToggleServiceTypeStatistics = async (serviceTypeId, password) => {
    try {
      const response = await axios.patch(`${API}/service-types/${serviceTypeId}/statistics?password=${password}`, {}, getAuthHeaders());
      toast.success(response.data.message);
      fetchServiceTypes();
      fetchStatistics();
      fetchReadingTypeStatistics();
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteServiceType = async (password) => {
    if (!serviceTypeToDelete) return;

    try {
      await axios.delete(`${API}/service-types/${serviceTypeToDelete.id}?password=${password}`, getAuthHeaders());
      
      toast.success(t('serviceTypeDeletedSuccessfully'));
      setIsDeleteServiceTypeDialogOpen(false);
      setServiceTypeToDelete(null);
      fetchServiceTypes();
      fetchReadingTypes();
      // Auto-refresh reading type statistics after deletion
      fetchReadingTypeStatistics();
    } catch (error) {
      throw error;
    }
  };

  const handleReorderServiceType = async (index, direction) => {
    const newList = [...serviceTypes];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= newList.length) return;
    [newList[index], newList[swapIndex]] = [newList[swapIndex], newList[index]];
    const order = newList.map((item, i) => ({ id: item.id, display_order: i }));
    setServiceTypes(newList);
    try {
      await axios.put(`${API}/service-types/reorder`, order, getAuthHeaders());
      toast.success('تم إعادة الترتيب بنجاح');
    } catch (error) {
      toast.error('خطأ في إعادة الترتيب');
      fetchServiceTypes();
    }
  };

  // Bulk Assignment Functions
  const handleSelectAllDeacons = () => {
    try {
      const filteredDeacons = deacons.filter(deacon => 
        deacon.name.toLowerCase().includes(bulkDeaconSearch.toLowerCase())
      );
      const newSelected = new Set([...selectedDeacons]);
      filteredDeacons.forEach(deacon => {
        if (deacon && deacon.id) {
          newSelected.add(deacon.id);
        }
      });
      setSelectedDeacons(newSelected);
      console.log('Selected all:', filteredDeacons.length, 'deacons');
    } catch (error) {
      console.error('Error selecting all deacons:', error);
    }
  };

  const handleDeselectAllDeacons = () => {
    try {
      setSelectedDeacons(new Set());
      console.log('Deselected all deacons');
    } catch (error) {
      console.error('Error deselecting all deacons:', error);
    }
  };

  const handleDeaconToggle = (deaconId) => {
    try {
      if (!deaconId) return;
      
      const newSelected = new Set(selectedDeacons);
      if (newSelected.has(deaconId)) {
        newSelected.delete(deaconId);
        console.log('Deselected deacon:', deaconId);
      } else {
        newSelected.add(deaconId);
        console.log('Selected deacon:', deaconId);
      }
      setSelectedDeacons(newSelected);
    } catch (error) {
      console.error('Error toggling deacon:', error);
    }
  };

  const handleBulkAssignment = async () => {
    // Validation
    if (!bulkReadingType || selectedDeacons.size === 0) {
      toast.error(t('pleaseSelectServiceAndDeacon'));
      return;
    }

    setIsSubmittingBulk(true);
    
    try {
      const formattedDate = formatDateForAPI(bulkAssignmentDate);
      const deaconIds = Array.from(selectedDeacons);
      
      console.log('Bulk assignment request:', {
        date: formattedDate,
        reading_type: bulkReadingType,
        deacon_ids: deaconIds,
        api_url: `${API}/assignments/bulk`
      });

      // Use axios with proper Authorization header
      const response = await axios.post(`${API}/assignments/bulk`, {
        date: formattedDate,
        reading_type: bulkReadingType,
        deacon_ids: deaconIds
      }, getAuthHeaders());
      
      console.log('Response received:', response.status, response.data);

      const result = response.data;
      console.log('Success result:', result);
      
      // Show detailed success message (with warnings if any)
      if (result.skipped_count > 0) {
        // Show success with warning information
        toast.success(result.message, { duration: 6000 });
      } else {
        // Show pure success message
        toast.success(result.message || t('savedSuccessfully'));
      }
      
      // Reset form
      setSelectedDeacons(new Set());
      setBulkDeaconSearch("");
      
      // Refresh data (don't let errors here affect the success message)
      try {
        fetchDeacons();
        if (formatDateForAPI(selectedDate) === formattedDate) {
          fetchAssignmentsForDate(selectedDate);
        }
        // Auto-refresh statistics after bulk assignment (silent - no error toast)
        fetchReadingTypeStatistics(false);
        fetchStatistics();
        // Trigger weekly schedule refresh (recently served deacons get deferred)
        setScheduleRefreshKey(k => k + 1);
      } catch (refreshError) {
        console.error('Error refreshing data after bulk assignment:', refreshError);
        // Don't show error to user - the assignment was successful
      }
      
    } catch (error) {
      console.error('خطأ في التسجيل الجماعي:', error);
      
      // Better error handling for different error types
      let errorMessage = "حدث خطأ غير متوقع";
      
      if (error.response && error.response.data && error.response.data.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(t('errorOccurred') + ": " + errorMessage);
    } finally {
      setIsSubmittingBulk(false);
    }
  };

  const showReadingDetails = (deaconStats) => {
    setSelectedDeaconStats(deaconStats);
    setIsReadingDetailsDialogOpen(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
      {/* Enhanced Loading Overlay */}
      {isLoadingData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 flex flex-col items-center shadow-xl max-w-sm">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mb-4"></div>
            <div className="text-lg font-semibold text-amber-800 mb-2">جاري تحميل البيانات...</div>
            <div className="text-sm text-gray-600 text-center">
              {networkStatus === 'offline' ? 
                "لا يوجد اتصال بالإنترنت - انتظار الاتصال..." :
                appHealth === 'unhealthy' ? 
                "الخادم بطيء - جاري المحاولة..." :
                "قد يستغرق التحميل بضع ثواني عند فتح التطبيق لأول مرة"
              }
            </div>
            
            {networkStatus === 'offline' && (
              <div className="mt-3 px-3 py-1 bg-red-100 text-red-800 rounded text-xs">
                🔴 غير متصل
              </div>
            )}
            {appHealth === 'unhealthy' && networkStatus === 'online' && (
              <div className="mt-3 px-3 py-1 bg-yellow-100 text-yellow-800 rounded text-xs">
                ⚠️ خادم بطيء
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Network/Health Status Bar */}
      {(networkStatus === 'offline' || appHealth === 'unhealthy') && !isLoadingData && (
        <div className={`fixed top-0 left-0 right-0 text-white text-center py-2 z-40 ${
          networkStatus === 'offline' ? 'bg-red-600' : 'bg-yellow-600'
        }`}>
          {networkStatus === 'offline' ? 
            "🔴 لا يوجد اتصال بالإنترنت" : 
            "⚠️ اتصال ضعيف بالخادم"
          }
          <button 
            onClick={() => window.location.reload()}
            className="ml-3 text-sm underline hover:no-underline"
          >
            إعادة تحميل
          </button>
        </div>
      )}
      
      {/* Update Available Banner */}
      {updateAvailable && (
        <div data-testid="update-available-banner" className="fixed top-0 left-0 right-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-center py-3 z-50 shadow-lg">
          <div className="flex items-center justify-center gap-3">
            <RefreshCw className="w-4 h-4 animate-spin" />
            <span className="font-medium">تحديث جديد متاح! يرجى تحديث الصفحة للحصول على أحدث إصدار.</span>
            <button 
              data-testid="refresh-now-btn"
              onClick={() => window.location.reload()}
              className="bg-white text-blue-700 px-4 py-1 rounded-full text-sm font-bold hover:bg-blue-50 transition-colors"
            >
              تحديث الآن
            </button>
            <button 
              onClick={() => { setUpdateAvailable(false); localStorage.setItem('update_banner_dismissed', new Date().toISOString()); }}
              className="mr-2 text-blue-200 hover:text-white"
            >
              ✕
            </button>
          </div>
        </div>
      )}
      
      {/* Offline Message */}
      {showOfflineMessage && !isOnline && (
        <div className="fixed top-0 left-0 right-0 bg-red-600 text-white text-center py-2 z-40">
          لا يوجد اتصال بالإنترنت - قد لا تعمل بعض الوظائف
          <button 
            onClick={() => setShowOfflineMessage(false)}
            className="ml-2 text-red-200 hover:text-white"
          >
            ✕
          </button>
        </div>
      )}
      {/* Offline indicator */}
      {showOfflineMessage && (
        <div className="bg-red-500 text-white text-center p-2">
          <AlertTriangle className="w-4 h-4 inline mr-2" />
          لا يوجد اتصال بالإنترنت - البيانات قد لا تكون محدثة
        </div>
      )}

      <div className="container mx-auto p-6 max-w-7xl">
        {/* Header - hidden when content is active */}
        <div className={`bg-white/90 backdrop-blur-sm border-b border-amber-200 shadow-sm mb-8 -mx-6 px-6 py-4 ${activeView ? 'hidden' : ''}`}>
          <div className="flex items-center justify-center">
            <div className="text-center">
              {/* Two Logos Side by Side */}
              <div className="flex justify-center items-center gap-4 mb-3">
                <img 
                  src="/diocese-logo.png" 
                  alt="Diocese Logo" 
                  className="w-16 h-16 rounded-full shadow-lg border-2 border-amber-200 bg-white"
                />
                <img 
                  src="/icon-192.png" 
                  alt="Church Logo" 
                  className="w-16 h-16 rounded-full shadow-lg border-2 border-amber-200"
                />
              </div>
              {/* البطريركية والإيبارشية */}
              <p className="text-purple-700 font-semibold text-sm mb-1">{t('patriarchate')}</p>
              <p className="text-purple-600 text-xs mb-3">{t('diocese')}</p>
              
              {/* اسم الكنيسة */}
              <h1 className="text-2xl md:text-3xl font-bold text-amber-900 mb-1">
                {churchInfo ? churchInfo.church_name : "Kirche der Hl. Maria & Hl.Philopater Mercurius Unna Massen"}
              </h1>
              {churchInfo && churchInfo.church_location && (
                <p className="text-amber-600 text-lg font-medium">{churchInfo.church_location}</p>
              )}
              <p className="text-amber-700 font-medium">خدمة الشموسية</p>
              <p className="text-sm text-amber-600 mt-1">تسجيل الخدمات والقراءات</p>
            </div>
          </div>
        </div>
        <div className={`text-center mb-8 ${activeView ? 'hidden' : ''}`}>
          
          {/* Today's date display */}
          <div className="mt-4 p-6 bg-gradient-to-r from-amber-100 to-orange-100 rounded-lg shadow-md max-w-2xl mx-auto">
            <h3 className="text-xl font-bold text-amber-900 mb-4 text-center">{getDayName(today)} - {t('today')}</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-lg border border-amber-200">
                <div className="text-center">
                  <div className="font-semibold text-amber-800 mb-2">{t('gregorianCalendar')}</div>
                  <div className="text-2xl font-bold text-amber-900 number-display">{formatTodayGregorian}</div>
                </div>
              </div>
              <div className="bg-white p-4 rounded-lg border border-amber-200">
                <div className="text-center">
                  <div className="font-semibold text-amber-800 mb-2">{t('copticCalendar')}</div>
                  <div className="text-xl font-bold text-amber-900">
                    <span className="number-display text-2xl">{copticToday.day}</span>
                    <span className="mx-2 text-lg">{copticToday.month}</span>
                    <span className="number-display text-xl">{copticToday.year}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Tabs value={activeView || "assignments"} onValueChange={(val) => setActiveView(val)} className="w-full">
          <div className="grid lg:grid-cols-4 gap-6 mb-6">
            {/* Sidebar with Menu - hidden when content is active */}
            <div className={`lg:col-span-1 ${activeView ? 'hidden' : ''}`}>
              <div className="flex flex-col h-auto w-full space-y-2">
                {[
                  { value: "assignments", icon: <BookOpen className="w-4 h-4" />, label: t('assignments'), show: true },
                  { value: "bulk-assignments", icon: <Plus className="w-4 h-4" />, label: t('bulkAttendance'), show: true },
                  { value: "deacons", icon: <Users className="w-4 h-4" />, label: t('deaconManagementTab'), show: isAdmin() },
                  { value: "service-types", icon: <Settings className="w-4 h-4" />, label: t('serviceTypes'), show: isAdmin() },
                  { value: "weekly-schedule", icon: <CalendarIcon className="w-4 h-4" />, label: language === 'de' ? 'Wochenplan' : 'الجدول الأسبوعي', show: true },
                  { value: "statistics", icon: <BarChart3 className="w-4 h-4" />, label: t('statistics'), show: true },
                  { value: "service-management", icon: <BookOpen className="w-4 h-4" />, label: t('serviceManagement'), show: isAdmin() },
                  { value: "data-management", icon: <Database className="w-4 h-4" />, label: t('dataManagement'), show: isAdmin() },
                  { value: "how-to-use", icon: <BookOpen className="w-4 h-4" />, label: language === 'de' ? 'Benutzerhandbuch' : 'طريقة الاستخدام', show: true },
                  { value: "contact-us", icon: <MessageCircle className="w-4 h-4" />, label: language === 'de' ? 'Kontaktieren Sie uns' : 'تواصل معنا', show: true },
                ].filter(item => item.show).map(item => (
                  <button key={item.value} onClick={() => setActiveView(item.value)}
                    className="w-full flex items-center justify-start gap-2 py-3 px-4 rounded-lg text-sm font-medium transition-all bg-white border border-amber-200 text-amber-800 hover:bg-amber-50 hover:border-amber-300"
                    data-testid={`menu-${item.value}`}>
                    {item.icon}
                    {item.label}
                  </button>
                ))}
                
                {/* Divider */}
                <div className="border-t border-amber-200 my-3"></div>
                
                {/* User Info & Actions */}
                <div className="space-y-2 px-1">
                  {/* User Name */}
                  <div className="text-center py-2 bg-amber-50 rounded-lg">
                    <span className="text-sm text-amber-700 font-medium">
                      👤 {user?.full_name || user?.username}
                    </span>
                  </div>
                  
                  {/* Church Settings Button */}
                  {isAdmin() && (
                  <button
                    onClick={openChurchSettingsDialog}
                    className="w-full flex items-center gap-2 p-3 text-amber-700 hover:text-amber-900 hover:bg-amber-100 rounded-lg transition-colors text-sm"
                  >
                    <Settings className="w-4 h-4" />
                    {t('churchSettings')}
                  </button>
                  )}
                  
                  {/* Refresh Data Button */}
                  <button
                    onClick={() => {
                      setIsLoadingData(true);
                      Promise.all([
                        fetchDeacons(0),
                        fetchReadingTypes(0), 
                        fetchServiceTypes(0),
                        fetchStatistics(0)
                      ]).finally(() => {
                        setIsLoadingData(false);
                        toast.success(t('savedSuccessfully'), { duration: 2000 });
                      });
                    }}
                    className="w-full flex items-center gap-2 p-3 text-amber-700 hover:text-amber-900 hover:bg-amber-100 rounded-lg transition-colors text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {t('reloadData')}
                  </button>
                  
                  {/* Logout Button */}
                  <button
                    onClick={() => {
                      if (window.confirm('هل أنت متأكد من تسجيل الخروج؟')) {
                        logout();
                      }
                    }}
                    className="w-full flex items-center gap-2 p-3 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors text-sm"
                  >
                    <LogOut className="w-4 h-4" />
                    {t('logout')}
                  </button>
                  
                  {/* Language Toggle Button */}
                  <button
                    onClick={toggleLanguage}
                    className="w-full flex items-center gap-2 p-3 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors text-sm"
                    title={t('language')}
                  >
                    <Globe className="w-4 h-4" />
                    {language === 'ar' ? 'Deutsch' : 'العربية'}
                  </button>
                </div>
              </div>
            </div>

            {/* Content Area - shown when a menu item is selected */}
            <div className={`${activeView ? 'lg:col-span-4' : 'hidden'}`}>
              {/* Back Button */}
              {activeView && (
                <button onClick={() => setActiveView(null)}
                  className="mb-4 flex items-center gap-2 px-4 py-2 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-lg transition-colors font-medium text-sm"
                  data-testid="back-to-menu-btn">
                  <ArrowRight className="w-4 h-4" />
                  {language === 'de' ? 'Zurück zum Menü' : 'رجوع للقائمة'}
                </button>
              )}

          {/* Assignments Tab */}
          <TabsContent value="assignments">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Calendar */}
              <Card className="shadow-lg border-amber-200">
                <CardHeader className="bg-amber-100">
                  <CardTitle className="flex items-center gap-2 text-amber-900">
                    <CalendarIcon className="w-5 h-5" />
                    {t('selectCalendarDate')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    className="rounded-md border border-amber-200"
                  />
                  <div className="mt-4 p-3 bg-amber-50 rounded-lg">
                    <div className="text-sm font-semibold text-amber-800 mb-2">{t('selectedDate')}:</div>
                    <div className="text-sm text-gray-700">
                      <div>ميلادي: <span className="number-display">{formatDateForDisplay(selectedDate)}</span></div>
                      <div>قبطي: <span className="number-display">
                        {toCopticDate(selectedDate).day} {toCopticDate(selectedDate).month} {toCopticDate(selectedDate).year}
                      </span></div>
                    </div>
                  </div>

                  {/* Saint of the Day */}
                  {saintOfDay && (
                    <div className="mt-4 p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg border-2 border-amber-200 shadow-sm">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-2xl">✝️</span>
                        <h3 className="text-sm font-bold text-amber-900">
                          {saintOfDay.saints?.length > 1 ? 'تذكارات اليوم' : 'قديس اليوم'}
                        </h3>
                      </div>
                      <div className="space-y-2">
                        <div className="text-sm mb-3">
                          <span className="font-semibold text-amber-800">التقويم القبطي: </span>
                          <span className="text-gray-700 number-display">{saintOfDay.coptic_date?.formatted}</span>
                        </div>
                        {/* عرض عناوين التذكارات فقط */}
                        <div className="space-y-1">
                          {saintOfDay.saints?.map((item, index) => (
                            <div key={index} className="flex items-start gap-2">
                              <span className="text-amber-600 mt-0.5">•</span>
                              <p className="text-sm text-amber-900">{item.saint}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Service Assignment */}
              <Card className="lg:col-span-2 shadow-lg border-amber-200">
                <CardHeader className="bg-amber-100 flex flex-row items-center justify-between">
                  <CardTitle className="text-amber-900">
                    تسجيل الخدمات والقراءات - <span className="date-display">{formatDateForDisplay(selectedDate)}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {/* Add new assignment form */}
                  <div className="bg-amber-50 p-4 rounded-lg mb-6">
                    <h3 className="font-semibold text-amber-900 mb-4">{t('newAssignment')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-amber-800 mb-2">نوع القراءة</label>
                        <Select value={selectedReadingType} onValueChange={setSelectedReadingType}>
                          <SelectTrigger className="border-amber-200">
                            <SelectValue placeholder={t('selectReadingType')} />
                          </SelectTrigger>
                          <SelectContent>
                            {serviceTypes && Array.isArray(serviceTypes) && serviceTypes.map((type) => (
                              <SelectItem key={type.id} value={type.name}>
                                {type.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-amber-800 mb-2">الشماس</label>
                        
                        {/* Mobile-specific deacon selection */}
                        {isMobile ? (
                          <div className="space-y-2">
                            <Button
                              type="button"
                              variant="outline" 
                              onClick={() => setShowMobileSearch(!showMobileSearch)}
                              className="w-full justify-between border-amber-200"
                            >
                              {selectedDeaconId 
                                ? deacons.find(d => d.id === selectedDeaconId)?.name || t('selectDeacon')
                                : t('selectDeacon')
                              }
                              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </Button>
                            
                            {showMobileSearch && (
                              <div className="border border-amber-200 rounded-lg bg-white shadow-lg max-h-64 overflow-hidden">
                                <div className="p-3 border-b border-gray-200">
                                  <input
                                    type="text"
                                    placeholder="بحث عن شماس..."
                                    value={deaconSearch}
                                    onChange={(e) => setDeaconSearch(e.target.value)}
                                    className="w-full px-3 py-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                    autoComplete="off"
                                    style={{ fontSize: '16px' }}
                                    autoFocus
                                  />
                                </div>
                                <div className="max-h-48 overflow-y-auto">
                                  {deacons && Array.isArray(deacons) ? 
                                    deacons
                                      .filter(deacon => 
                                        !deaconSearch || deacon.name.toLowerCase().includes(deaconSearch.toLowerCase())
                                      )
                                      .map((deacon) => (
                                        <button
                                          key={deacon.id}
                                          type="button"
                                          className="w-full text-right px-3 py-2 hover:bg-amber-50 focus:bg-amber-50 border-b border-gray-100 last:border-b-0"
                                          onClick={() => {
                                            setSelectedDeaconId(deacon.id);
                                            setShowMobileSearch(false);
                                            setDeaconSearch("");
                                          }}
                                        >
                                          {deacon.name}
                                        </button>
                                      ))
                                    : null
                                  }
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          /* Desktop version */
                          <Select 
                            value={selectedDeaconId} 
                            onValueChange={setSelectedDeaconId}
                            onOpenChange={(open) => {
                              if (!open) {
                                setTimeout(() => setDeaconSearch(""), 100);
                              }
                            }}
                          >
                            <SelectTrigger className="border-amber-200">
                              <SelectValue placeholder={t('selectDeacon')} />
                            </SelectTrigger>
                            <SelectContent>
                              {/* Search box */}
                              <div 
                                className="sticky top-0 bg-white p-2 border-b border-gray-200 z-10"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                                onTouchStart={(e) => e.stopPropagation()}
                                onTouchEnd={(e) => e.stopPropagation()}
                              >
                                <input
                                  type="text"
                                  placeholder="بحث عن شماس..."
                                  value={deaconSearch}
                                  onChange={(e) => setDeaconSearch(e.target.value)}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onClick={(e) => e.stopPropagation()}
                                  onFocus={(e) => {
                                    e.stopPropagation();
                                    e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                  }}
                                  onBlur={(e) => {
                                    setTimeout(() => {
                                      if (!e.target.value) {
                                        // Only allow blur if no search text
                                      }
                                    }, 100);
                                  }}
                                  onTouchStart={(e) => {
                                    e.stopPropagation();
                                    e.target.focus();
                                  }}
                                  onTouchEnd={(e) => e.stopPropagation()}
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                  autoComplete="off"
                                  inputMode="text"
                                  style={{ 
                                    fontSize: '16px',
                                    WebkitAppearance: 'none',
                                    touchAction: 'manipulation'
                                  }}
                                />
                              </div>
                              {/* Filtered deacons */}
                              {deacons && Array.isArray(deacons) ? 
                                deacons
                                  .filter(deacon => 
                                    !deaconSearch || deacon.name.toLowerCase().includes(deaconSearch.toLowerCase())
                                  )
                                  .map((deacon) => (
                                    <SelectItem key={deacon.id} value={deacon.id}>
                                      {deacon.name}
                                    </SelectItem>
                                  ))
                                : null
                              }
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <Button 
                        onClick={handleAddAssignment}
                        className="bg-amber-600 hover:bg-amber-700 w-full md:w-auto"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        إضافة التوزيع
                      </Button>
                    </div>
                  </div>

                  {/* Current assignments display */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-amber-900">{t('assignments')}:</h3>
                      <div className="flex gap-2 items-center flex-wrap">
                        {/* Print Button */}
                        {detailedAssignments && detailedAssignments.length > 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handlePrintAssignments('day')}
                            className="border-blue-300 text-blue-700 hover:bg-blue-50"
                            data-testid="print-assignments-btn"
                          >
                            <Printer className="w-3 h-3 ml-1" />
                            طباعة
                          </Button>
                        )}
                        {isAdmin() && detailedAssignments && detailedAssignments.length > 0 && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={toggleSelectAll}
                              className="border-amber-300 text-amber-700 hover:bg-amber-50"
                            >
                              {selectedAssignments.size === detailedAssignments.length ? t('deselectAll') : t('selectAll')}
                            </Button>
                            {selectedAssignments.size > 0 && (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => executeWithPassword(
                                  handleBulkDeleteAssignments,
                                  t('delete'),
                                  `هل أنت متأكد من حذف ${selectedAssignments.size} توزيع؟`
                                )}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                <Trash2 className="w-3 h-3 mr-1" />
                                حذف المحدد ({selectedAssignments.size})
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    {detailedAssignments && Array.isArray(detailedAssignments) && detailedAssignments.length > 0 ? (
                      detailedAssignments.map((assignment) => (
                        <div key={assignment.id} className="flex items-center gap-3 bg-green-50 p-3 rounded-lg border border-green-200">
                          {isAdmin() && (
                            <input
                              type="checkbox"
                              checked={selectedAssignments.has(assignment.id)}
                              onChange={() => toggleAssignmentSelection(assignment.id)}
                              className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500 cursor-pointer"
                            />
                          )}
                          <div className="flex-1">
                            <span className="font-medium text-green-800">{assignment.reading_type}</span>
                            <span className="text-green-600 mx-2">←</span>
                            <span className="text-green-700">{assignment.deacon_name}</span>
                          </div>
                          {(isAdmin() || assignment.created_by === user?.username) && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setEditingAssignment(assignment);
                                  setIsEditDialogOpen(true);
                                }}
                                className="border-green-300 text-green-700 hover:bg-green-50"
                              >
                                <Edit className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => executeWithPassword(
                                  (password) => handleDeleteAssignment(assignment.id, password),
                                  t('delete'),
                                  "هل أنت متأكد من حذف هذا التوزيع؟"
                                )}
                                className="border-red-300 text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 text-center py-4">لا توجد توزيعات لهذا التاريخ</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Bulk Assignment Section removed from assignments tab */}
          </TabsContent>

          {/* Bulk Assignment Tab */}
          <TabsContent value="bulk-assignments">
            <div className="grid gap-6">
              
              {/* Green bulk interface removed - keeping only the simple one */}
              
              <Card className="shadow-lg border-amber-200">
                <CardHeader className="bg-amber-100">
                  <CardTitle className="flex items-center gap-2 text-amber-900">
                    <Plus className="w-5 h-5" />
                    تسجيل حضور جماعي
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid gap-6">
                    {/* Date and Reading Type Selection */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">التاريخ</label>
                        <Input
                          type="date"
                          value={formatDateForAPI(bulkAssignmentDate)}
                          onChange={(e) => setBulkAssignmentDate(new Date(e.target.value))}
                          className="w-full"
                        />
                        <div className="text-xs text-gray-500 mt-1">
                          التاريخ المحدد: {formatDateForDisplay(bulkAssignmentDate)}
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-gray-700">نوع الخدمة/القراءة</label>
                          <Select value={bulkReadingType} onValueChange={setBulkReadingType}>
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder={t('selectServiceType')} />
                            </SelectTrigger>
                            <SelectContent>
                              {readingTypes.map((type) => (
                                <SelectItem key={type} value={type}>
                                  {type}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="text-center space-y-2 pt-4">
                          <div className="text-sm text-gray-600">
                            {t('selectedDeacons')} {selectedDeacons.size} {language === 'ar' ? 'شماس' : 'Diakon(e)'}
                          </div>
                          <Button
                            onClick={handleBulkAssignment}
                            disabled={isSubmittingBulk || selectedDeacons.size === 0 || !bulkReadingType}
                            className="w-full bg-amber-600 hover:bg-amber-700"
                          >
                            {isSubmittingBulk ? t('saving') : `${t('registerAttendance')} ${selectedDeacons.size}`}
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Deacon Selection */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-800">{t('selectDeacons')}</h3>
                        <div className="flex gap-2">
                          <Button
                            onClick={handleSelectAllDeacons}
                            variant="outline"
                            size="sm"
                            className="text-xs"
                          >
                            {t('selectAll')}
                          </Button>
                          <Button
                            onClick={handleDeselectAllDeacons}
                            variant="outline"
                            size="sm"
                            className="text-xs"
                          >
                            {t('deselectAll')}
                          </Button>
                        </div>
                      </div>

                      {/* Search */}
                      <div className="space-y-2">
                        <Input
                          type="text"
                          placeholder={t('searchDeacons')}
                          value={bulkDeaconSearch}
                          onChange={(e) => setBulkDeaconSearch(e.target.value)}
                          className="w-full"
                        />
                      </div>

                      {/* Deacons List with Checkboxes */}
                      <div className="max-h-96 overflow-y-auto border rounded-lg p-4 bg-gray-50">
                        <div className="grid gap-2">
                          {deacons
                            .filter(deacon => deacon.name.toLowerCase().includes(bulkDeaconSearch.toLowerCase()))
                            .map((deacon) => (
                              <label 
                                key={deacon.id} 
                                className="flex items-center gap-3 p-2 rounded hover:bg-white cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedDeacons.has(deacon.id)}
                                  onChange={() => handleDeaconToggle(deacon.id)}
                                  className="h-4 w-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                                />
                                <span className="flex-1 text-right">
                                  {deacon.name}
                                  {deacon.phone && (
                                    <span className="text-xs text-gray-500 block">
                                      {deacon.phone}
                                    </span>
                                  )}
                                </span>
                              </label>
                            ))
                          }
                        </div>
                        
                        {deacons.filter(deacon => 
                          deacon.name.toLowerCase().includes(bulkDeaconSearch.toLowerCase())
                        ).length === 0 && (
                          <div className="text-center text-gray-500 py-4">
                            {t('noDeaconsFound')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Deacons Management Tab */}
          <TabsContent value="deacons">
            <div className="grid gap-6">
              <Card className="shadow-lg border-amber-200">
                <CardHeader className="bg-amber-100 flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-amber-900">
                    <Users className="w-5 h-5" />
                    {t('deaconManagementTab')} ({deacons.length})
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => { setEditableRanks([...ranks]); setIsRanksDialogOpen(true); }}
                      className="border-amber-300 text-amber-700 hover:bg-amber-50"
                      data-testid="manage-ranks-btn"
                    >
                      إدارة الرتب
                    </Button>
                    <Button
                      onClick={() => setIsAddDeaconOpen(true)}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {t('addDeacon')}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  {/* Sort Options */}
                  <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <span className="text-sm font-medium text-amber-700">{language === 'ar' ? 'ترتيب حسب:' : 'Sortieren:'}</span>
                    {[
                      { value: "name", label: language === 'ar' ? "الاسم" : "Name" },
                      { value: "rank", label: language === 'ar' ? "الرتبة" : "Rang" },
                      { value: "participation_desc", label: language === 'ar' ? "الأكثر مشاركة" : "Meiste" },
                      { value: "participation_asc", label: language === 'ar' ? "الأقل مشاركة" : "Wenigste" },
                      { value: "date_new", label: language === 'ar' ? "الأحدث" : "Neueste" },
                      { value: "date_old", label: language === 'ar' ? "الأقدم" : "Älteste" },
                      { value: "active", label: language === 'ar' ? "مفعّل" : "Aktiv" },
                      { value: "inactive", label: language === 'ar' ? "غير مفعّل" : "Inaktiv" },
                    ].map(opt => (
                      <button key={opt.value} onClick={() => setDeaconSortBy(opt.value)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                          deaconSortBy === opt.value
                            ? 'bg-amber-500 text-white shadow-sm'
                            : 'bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100'
                        }`} data-testid={`sort-${opt.value}`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {/* Search */}
                  <div className="mb-4">
                    <input
                      type="text"
                      placeholder={language === 'ar' ? 'بحث بالاسم...' : 'Nach Name suchen...'}
                      value={deaconSearch}
                      onChange={(e) => setDeaconSearch(e.target.value)}
                      className="w-full px-4 py-2 border border-amber-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
                      data-testid="deacon-search-input"
                    />
                  </div>
                  {isLoadingData ? (
                    <p className="text-center py-8 text-gray-500">جاري التحميل...</p>
                  ) : deacons && deacons.length > 0 ? (
                    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                      {getSortedDeacons().filter(d => !deaconSearch || d.name.toLowerCase().includes(deaconSearch.toLowerCase())).map((deacon) => (
                        <div key={deacon.id} className="bg-white p-4 rounded-lg border border-amber-200 shadow-sm">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <button onClick={() => openDeaconProfile(deacon)} className="font-medium text-amber-900 hover:text-amber-600 hover:underline transition-colors cursor-pointer" data-testid={`deacon-name-${deacon.id}`}>{deacon.name}</button>
                                {deacon.schedule_active === false && (
                                  <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">
                                    {language === 'ar' ? 'معطل' : 'Inaktiv'}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline" className="text-xs border-amber-200 text-amber-700">
                                  {language === 'ar' ? 'مشاركات' : 'Teilnahmen'}: {deacon.total_participation || 0}
                                </Badge>
                              </div>
                              <select
                                data-testid={`rank-select-${deacon.id}`}
                                value={deacon.rank || ""}
                                onChange={(e) => handleUpdateDeaconRank(deacon.id, e.target.value || null)}
                                className="mt-1 text-xs border border-amber-200 rounded px-2 py-1 bg-amber-50 text-amber-800 w-full focus:outline-none focus:ring-1 focus:ring-amber-400"
                              >
                                <option value="">-- الرتبة --</option>
                                {ranks.map(r => (
                                  <option key={r.id} value={r.name}>{r.name}</option>
                                ))}
                              </select>
                              {deacon.phone && (
                                <p className="text-sm text-gray-600 mt-1">
                                  📞 {deacon.phone}
                                </p>
                              )}
                              {deacon.address && (
                                <p className="text-sm text-gray-600 mt-1">
                                  📍 {deacon.address}
                                </p>
                              )}
                              <p className="text-sm text-gray-500 mt-1">
                                تاريخ الإضافة: <span className="number-display">{formatDateForDisplay(new Date(deacon.created_at))}</span>
                              </p>
                            </div>
                            {isAdmin() && (
                              <div className="flex flex-col items-end gap-1.5">
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setEditingDeacon({ 
                                        ...deacon, 
                                        newName: deacon.name,
                                        newPhone: deacon.phone || "",
                                        newAddress: deacon.address || ""
                                      });
                                      setIsEditDeaconDialogOpen(true);
                                    }}
                                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                                  >
                                    <Edit className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => executeWithPassword(
                                      (password) => handleDeleteDeacon(deacon.id, password),
                                      t('deleteDeacon'),
                                      t('confirmDelete')
                                    )}
                                    className="border-red-300 text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                                <button
                                  onClick={async () => {
                                    try {
                                      const res = await axios.put(`${API}/deacons/${deacon.id}/schedule-toggle`, {}, getAuthHeaders());
                                      const updated = deacons.map(d => d.id === deacon.id ? {...d, schedule_active: res.data.schedule_active} : d);
                                      setDeacons(updated);
                                      toast.success(res.data.schedule_active 
                                        ? (language === 'ar' ? 'تم تفعيل المشاركة في الجدول' : 'Zeitplan aktiviert')
                                        : (language === 'ar' ? 'تم تعطيل المشاركة في الجدول' : 'Zeitplan deaktiviert'));
                                    } catch(e) { toast.error(language === 'ar' ? 'خطأ' : 'Fehler'); }
                                  }}
                                  className={`text-xs px-2 py-1 rounded-full border transition-all ${
                                    deacon.schedule_active !== false
                                      ? 'bg-green-50 border-green-300 text-green-700 hover:bg-green-100'
                                      : 'bg-gray-50 border-gray-300 text-gray-500 hover:bg-gray-100'
                                  }`}
                                  data-testid={`schedule-toggle-${deacon.id}`}
                                >
                                  {deacon.schedule_active !== false 
                                    ? (language === 'ar' ? 'مفعّل بالجدول' : 'Im Zeitplan')
                                    : (language === 'ar' ? 'معطّل بالجدول' : 'Nicht im Zeitplan')}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center py-8 text-gray-500">لا توجد شمامسة مسجلين</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Deacon Profile Dialog */}
          <Dialog open={deaconProfileOpen} onOpenChange={setDeaconProfileOpen}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" dir="rtl">
              <DialogHeader>
                <DialogTitle className="text-amber-900 text-xl flex items-center gap-2">
                  {deaconProfileData?.deacon?.name || '...'}
                  {deaconProfileData?.deacon?.rank && (
                    <Badge className="bg-amber-100 text-amber-800 border-amber-300">{deaconProfileData.deacon.rank}</Badge>
                  )}
                </DialogTitle>
              </DialogHeader>
              {deaconProfileLoading ? (
                <p className="text-center py-8 text-gray-500">جاري التحميل...</p>
              ) : deaconProfileData ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-4 bg-amber-50 rounded-lg p-4 border border-amber-100">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-amber-700">{deaconProfileData.assignments.length}</p>
                      <p className="text-xs text-amber-600">إجمالي المشاركات</p>
                    </div>
                    <div className="h-10 w-px bg-amber-200"></div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-amber-700">
                        {[...new Set(deaconProfileData.assignments.map(a => a.reading_type))].length}
                      </p>
                      <p className="text-xs text-amber-600">أنواع خدمات</p>
                    </div>
                    <div className="h-10 w-px bg-amber-200"></div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-amber-700">
                        {deaconProfileData.assignments.length > 0 ? new Date(deaconProfileData.assignments[0].date).toLocaleDateString('ar-EG', {day: 'numeric', month: 'short'}) : '-'}
                      </p>
                      <p className="text-xs text-amber-600">آخر مشاركة</p>
                    </div>
                  </div>

                  {deaconProfileData.assignments.length > 0 ? (
                    <div className="border border-amber-100 rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-amber-50 text-amber-800">
                            <th className="py-2 px-3 text-right font-medium">التاريخ</th>
                            <th className="py-2 px-3 text-right font-medium">نوع الخدمة</th>
                            <th className="py-2 px-3 text-center font-medium">إجراءات</th>
                          </tr>
                        </thead>
                        <tbody>
                          {deaconProfileData.assignments.map((a, idx) => (
                            <tr key={a.id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-amber-50/30'}>
                              <td className="py-2 px-3 text-gray-700">
                                {new Date(a.date).toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' })}
                              </td>
                              <td className="py-2 px-3">
                                <select
                                  value={a.reading_type}
                                  onChange={async (e) => {
                                    const newType = e.target.value;
                                    try {
                                      await axios.put(`${API}/assignments/${a.id}/reading-type`, { reading_type: newType, password: "" }, getAuthHeaders());
                                      setDeaconProfileData(prev => ({
                                        ...prev,
                                        assignments: prev.assignments.map(x => x.id === a.id ? { ...x, reading_type: newType } : x)
                                      }));
                                      toast.success('تم تحديث نوع الخدمة');
                                    } catch { toast.error('خطأ في تحديث نوع الخدمة'); }
                                  }}
                                  className="text-xs border border-amber-200 rounded px-2 py-1 bg-white text-amber-700 font-medium cursor-pointer hover:border-amber-400 transition-colors"
                                  data-testid={`edit-type-${a.id}`}
                                >
                                  {readingTypes.map(rt => (
                                    <option key={rt} value={rt}>{rt}</option>
                                  ))}
                                </select>
                              </td>
                              <td className="py-2 px-3 text-center">
                                <button onClick={() => handleDeleteAssignment(a.id, "")}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded p-1 transition-colors"
                                  data-testid={`delete-assignment-${a.id}`} title="حذف">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-center py-6 text-gray-400">لا توجد مشاركات مسجلة</p>
                  )}
                </div>
              ) : null}
            </DialogContent>
          </Dialog>

          {/* Service Types Tab */}
          <TabsContent value="service-types">
            <Card className="shadow-lg border-amber-200">
              <CardHeader className="bg-amber-100 flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-amber-900">
                  <Settings className="w-5 h-5" />
                  أنواع الخدمات ({serviceTypes.length})
                </CardTitle>
                <Button
                  onClick={() => setIsServiceTypeDialogOpen(true)}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  إضافة نوع خدمة
                </Button>
              </CardHeader>
              <CardContent className="p-6">
                {serviceTypes && serviceTypes.length > 0 ? (
                  <div className="space-y-3">
                    {serviceTypes.map((type, index) => (
                      <div key={type.id} data-testid={`service-type-item-${index}`} className="bg-white p-4 rounded-lg border border-amber-200 shadow-sm flex items-center gap-3">
                        {/* Reorder buttons */}
                        <div className="flex flex-col gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            data-testid={`reorder-up-${index}`}
                            onClick={() => handleReorderServiceType(index, 'up')}
                            disabled={index === 0}
                            className="h-7 w-7 p-0 text-amber-600 hover:bg-amber-100 disabled:opacity-30"
                          >
                            <ChevronUp className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            data-testid={`reorder-down-${index}`}
                            onClick={() => handleReorderServiceType(index, 'down')}
                            disabled={index === serviceTypes.length - 1}
                            className="h-7 w-7 p-0 text-amber-600 hover:bg-amber-100 disabled:opacity-30"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </Button>
                        </div>
                        {/* Order number */}
                        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-800 font-bold text-sm number-display">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-medium text-amber-900">{type.name}</h3>
                          {type.description && (
                            <p className="text-sm text-gray-600 mt-1">{type.description}</p>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              executeWithPassword(
                                (password) => handleToggleServiceTypeStatistics(type.id, password),
                                t('statistics'),
                                t('statistics')
                              );
                            }}
                            className={`border-2 ${
                              type.include_in_statistics 
                                ? "border-green-300 text-green-700 hover:bg-green-50 bg-green-100" 
                                : "border-gray-300 text-gray-600 hover:bg-gray-50"
                            }`}
                            title={type.include_in_statistics ? t('enabled') : t('disabled')}
                          >
                            {type.include_in_statistics ? (
                              <BarChart3 className="w-3 h-3" />
                            ) : (
                              <BarChart3 className="w-3 h-3 opacity-40" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingServiceType(type);
                              setIsEditServiceTypeDialogOpen(true);
                            }}
                            className="border-amber-300 text-amber-700 hover:bg-amber-50"
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setServiceTypeToDelete(type);
                              executeWithPassword(
                                handleDeleteServiceType,
                                t('deleteServiceType'),
                                `هل أنت متأكد من حذف نوع الخدمة "${type.name}"؟`
                              );
                            }}
                            className="border-red-300 text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center py-8 text-gray-500">لا توجد أنواع خدمات</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Weekly Schedule Tab */}
          <TabsContent value="weekly-schedule">
            <WeeklyScheduleComponent 
              API={API} 
              token={token} 
              deacons={deacons}
              language={language}
              getAuthHeaders={getAuthHeaders}
              refreshKey={scheduleRefreshKey}
              serviceTypes={serviceTypes}
            />
          </TabsContent>

          {/* Statistics Tab */}
          <TabsContent value="statistics">
            <div className="space-y-6">
              {/* Deacons Statistics */}
              <Card className="shadow-lg border-amber-200">
                <CardHeader className="bg-amber-100">
                  <CardTitle className="flex items-center gap-2 text-amber-900">
                    <BarChart3 className="w-5 h-5" />
                    إحصائيات الشمامسة
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {/* Sort Options */}
                  <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <span className="text-sm font-medium text-amber-700">ترتيب حسب:</span>
                    {[
                      { value: "name", label: "الاسم" },
                      { value: "rank", label: "الرتبة" },
                      { value: "participation_desc", label: "الأكثر مشاركة" },
                      { value: "participation_asc", label: "الأقل مشاركة" },
                      { value: "date_new", label: "الأحدث" },
                      { value: "date_old", label: "الأقدم" },
                    ].map(opt => (
                      <button key={opt.value} onClick={() => setStatsSortBy(opt.value)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                          statsSortBy === opt.value
                            ? 'bg-amber-500 text-white shadow-sm'
                            : 'bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100'
                        }`} data-testid={`stats-sort-${opt.value}`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {statistics && statistics.length > 0 ? (
                    <Accordion type="single" collapsible className="space-y-2">
                      {getSortedStats().map((stat, index) => (
                        <AccordionItem 
                          key={index} 
                          value={`deacon-${index}`}
                          className="border border-amber-200 rounded-lg"
                        >
                          <AccordionTrigger className="px-4 py-3 hover:bg-amber-50">
                            <div className="flex items-center justify-between w-full mr-4">
                              <span className="font-medium text-amber-900">{stat.name}</span>
                              <Badge 
                                variant="outline" 
                                className="border-amber-300 text-amber-800 cursor-pointer hover:bg-amber-100"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  showReadingDetails(stat);
                                }}
                              >
                                <span className="number-display">{stat.total_readings}</span> قراءة
                              </Badge>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-4 pb-4">
                            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                              {Object.entries(stat.readings_by_type || {}).map(([type, count]) => (
                                count > 0 && (
                                  <div key={type} className="bg-green-50 p-3 rounded border border-green-200">
                                    <div className="flex justify-between items-center">
                                      <span className="text-green-800 font-medium">{type}</span>
                                      <Badge className="bg-green-100 text-green-800 border-green-300">
                                        <span className="number-display">{count}</span>
                                      </Badge>
                                    </div>
                                  </div>
                                )
                              ))}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  ) : (
                    <p className="text-center py-8 text-gray-500">لا توجد إحصائيات متاحة</p>
                  )}
                </CardContent>
              </Card>

              {/* Inactive Deacons */}
              <Card className="shadow-lg border-red-200">
                <CardHeader className="bg-red-100">
                  <CardTitle className="flex items-center gap-2 text-red-900">
                    <AlertCircle className="w-5 h-5" />
                    الشمامسة الذين يحتاجون لمزيد من المشاركة
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {inactiveDeacons && inactiveDeacons.length > 0 ? (
                    <Accordion type="single" collapsible className="space-y-2">
                      {inactiveDeacons.map((deacon, index) => (
                        <AccordionItem 
                          key={index} 
                          value={`inactive-${index}`}
                          className="border border-red-200 rounded-lg"
                        >
                          <AccordionTrigger className="px-4 py-3 hover:bg-red-50">
                            <div className="flex items-center justify-between w-full mr-4">
                              <div className="flex items-center gap-3">
                                <span className="font-medium text-red-900">{deacon.name}</span>
                                <Badge variant="outline" className="border-red-300 text-red-800">
                                  <span className="number-display">{deacon.total_readings}</span> قراءة
                                </Badge>
                              </div>
                              <div className="text-sm text-red-600">
                                {deacon.reason}
                              </div>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-4 pb-4">
                            <div className="space-y-3">
                              <div className="bg-red-50 p-3 rounded border border-red-200">
                                <div className="grid gap-2 md:grid-cols-2">
                                  <div>
                                    <span className="font-medium text-red-800">آخر مشاركة:</span>
                                    <div className="text-red-700 number-display">
                                      {deacon.last_reading_date ? formatDateForDisplay(new Date(deacon.last_reading_date)) : 'لم يشارك من قبل'}
                                    </div>
                                  </div>
                                  <div>
                                    <span className="font-medium text-red-800">منذ:</span>
                                    <div className="text-red-700 number-display">
                                      {deacon.days_since_last_reading ? `${deacon.days_since_last_reading} يوم` : 'غير محدد'}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {deacon.readings_by_type && Object.keys(deacon.readings_by_type).length > 0 && (
                                <div>
                                  <h4 className="font-medium text-red-800 mb-2">توزيع القراءات:</h4>
                                  <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                                    {Object.entries(deacon.readings_by_type).map(([type, count]) => (
                                      count > 0 && (
                                        <div key={type} className="bg-orange-50 p-2 rounded border border-orange-200">
                                          <div className="flex justify-between items-center">
                                            <span className="text-orange-800 text-sm">{type}</span>
                                            <Badge className="bg-orange-100 text-orange-800 border-orange-300">
                                              <span className="number-display">{count}</span>
                                            </Badge>
                                          </div>
                                        </div>
                                      )
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  ) : (
                    <p className="text-center py-8 text-gray-500">جميع الشمامسة نشطون!</p>
                  )}
                </CardContent>
              </Card>
              
              {/* Reading Type Statistics */}
              <Card className="shadow-lg border-purple-200">
                <CardHeader className="bg-purple-100">
                  <CardTitle className="flex items-center gap-2 text-purple-900">
                    <BarChart3 className="w-5 h-5" />
                    تصنيف القراءات حسب النوع
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {readingTypeStatistics && readingTypeStatistics.length > 0 ? (
                    <Accordion type="single" collapsible className="space-y-2">
                      {/* Show types with activities first */}
                      {readingTypeStatistics
                        .filter(stat => stat.total_count > 0)
                        .map((stat, index) => (
                        <AccordionItem 
                          key={index} 
                          value={`reading-type-${index}`}
                          className="border border-purple-200 rounded-lg"
                        >
                          <AccordionTrigger className="px-4 py-3 hover:bg-purple-50">
                            <div className="flex items-center justify-between w-full mr-4">
                              <span className="font-medium text-purple-900">{stat.reading_type}</span>
                              <Badge variant="outline" className="border-purple-300 text-purple-800">
                                <span className="number-display">{stat.total_count}</span> مرة
                              </Badge>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-4 pb-4">
                            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                              {Object.entries(stat.deacons || {}).map(([deaconName, count]) => (
                                <div key={deaconName} className="bg-purple-50 p-3 rounded border border-purple-200">
                                  <div className="flex justify-between items-center">
                                    <span className="text-purple-800 font-medium text-sm">{deaconName}</span>
                                    <Badge className="bg-purple-100 text-purple-800 border-purple-300">
                                      <span className="number-display">{count}</span>
                                    </Badge>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                      
                      {/* Show new service types with zero counts */}
                      {readingTypeStatistics
                        .filter(stat => stat.total_count === 0)
                        .length > 0 && (
                        <AccordionItem 
                          value="new-service-types"
                          className="border border-blue-200 rounded-lg"
                        >
                          <AccordionTrigger className="px-4 py-3 hover:bg-blue-50">
                            <div className="flex items-center justify-between w-full">
                              <span className="font-medium text-blue-700">أنواع خدمات جديدة (لم يتم استخدامها بعد)</span>
                              <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-semibold">
                                {readingTypeStatistics.filter(stat => stat.total_count === 0).length} نوع
                              </span>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-4 pb-4">
                            <div className="space-y-2">
                              {readingTypeStatistics
                                .filter(stat => stat.total_count === 0)
                                .map((stat, idx) => (
                                <div key={idx} className="flex items-center justify-between p-2 bg-blue-50 rounded">
                                  <span className="text-blue-800 font-medium">{stat.reading_type}</span>
                                  <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                                    جاهز للاستخدام ✨
                                  </span>
                                </div>
                              ))}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      )}
                      
                    </Accordion>
                  ) : (
                    <p className="text-center py-8 text-gray-500">لا توجد بيانات تصنيف متاحة</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Service Management Tab */}
          <TabsContent value="service-management">
            <Card className="shadow-lg border-amber-200">
              <CardHeader className="bg-amber-100">
                <CardTitle className="flex items-center gap-2 text-amber-900">
                  <BookOpen className="w-5 h-5" />
                  إدارة الخدمات - عرض وحذف التسجيلات حسب الخدمة
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {/* Service Type Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    اختر الخدمة لعرض جميع التسجيلات:
                  </label>
                  <Select 
                    value={selectedServiceType} 
                    onValueChange={(value) => {
                      setSelectedServiceType(value);
                      fetchAssignmentsByServiceType(value);
                    }}
                  >
                    <SelectTrigger className="w-full md:w-96">
                      <SelectValue placeholder={t('selectServiceType')} />
                    </SelectTrigger>
                    <SelectContent>
                      {readingTypes && readingTypes.length > 0 ? (
                        readingTypes.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="none" disabled>لا توجد خدمات</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Service Assignments Display */}
                {selectedServiceType && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between bg-amber-50 p-4 rounded-lg">
                      <div>
                        <h3 className="font-semibold text-amber-900 text-lg">
                          {selectedServiceType}
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">
                          {isLoadingServiceAssignments 
                            ? t('loading') 
                            : `عدد التسجيلات: ${serviceAssignments.length}`}
                        </p>
                      </div>
                      {isAdmin() && serviceAssignments.length > 0 && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={toggleSelectAllServiceAssignments}
                            className="border-amber-300 text-amber-700 hover:bg-amber-50"
                          >
                            {selectedServiceAssignments.size === serviceAssignments.length 
                              ? t('deselectAll') 
                              : "تحديد الكل"}
                          </Button>
                          {selectedServiceAssignments.size > 0 && (
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => executeWithPassword(
                                handleBulkDeleteServiceAssignments,
                                t('delete'),
                                `هل أنت متأكد من حذف ${selectedServiceAssignments.size} تسجيل من خدمة "${selectedServiceType}"؟`
                              )}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              حذف المحدد ({selectedServiceAssignments.size})
                            </Button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Assignments List */}
                    {isLoadingServiceAssignments ? (
                      <div className="text-center py-8 text-gray-500">
                        جاري التحميل...
                      </div>
                    ) : serviceAssignments.length > 0 ? (
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {serviceAssignments.map((assignment) => (
                          <div 
                            key={assignment.id} 
                            className="flex items-center gap-3 bg-white p-4 rounded-lg border border-gray-200 hover:border-amber-300 transition-colors"
                          >
                            {isAdmin() && (
                              <input
                                type="checkbox"
                                checked={selectedServiceAssignments.has(assignment.id)}
                                onChange={() => toggleServiceAssignmentSelection(assignment.id)}
                                className="w-5 h-5 text-amber-600 border-gray-300 rounded focus:ring-amber-500 cursor-pointer"
                              />
                            )}
                            <div className="flex-1 grid grid-cols-2 gap-4">
                              <div>
                                <span className="text-sm text-gray-500">التاريخ:</span>
                                <p className="font-medium text-gray-900 number-display">
                                  {formatDateForDisplay(new Date(assignment.date))}
                                </p>
                              </div>
                              <div>
                                <span className="text-sm text-gray-500">الشماس:</span>
                                <p className="font-medium text-amber-800">
                                  {assignment.deacon_name}
                                </p>
                              </div>
                            </div>
                            {(isAdmin() || assignment.created_by === user?.username) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => executeWithPassword(
                                  (password) => handleDeleteAssignment(assignment.id, password),
                                  t('delete'),
                                  "هل أنت متأكد من حذف هذا التسجيل؟"
                                )}
                                className="border-red-300 text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                        لا توجد تسجيلات لهذه الخدمة
                      </div>
                    )}
                  </div>
                )}

                {!selectedServiceType && (
                  <div className="text-center py-12 text-gray-500">
                    <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p>اختر خدمة من القائمة أعلاه لعرض جميع التسجيلات</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Data Management Tab */}
          <TabsContent value="data-management">
            {/* User Management - Super Admin Only */}
            {isSuperAdmin() && (
              <div className="mb-6">
                <UserManagement />
              </div>
            )}

            {/* Church User Management - Admin Only (not super admin) */}
            {isAdmin() && !isSuperAdmin() && (
              <div className="mb-6">
                <ChurchUserManagement />
              </div>
            )}

            {/* Profile Settings - All Users */}
            <div className="mb-6">
              <UserProfile />
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {/* Export Data */}
              <Card className="shadow-lg border-amber-200">
                <CardHeader className="bg-amber-100">
                  <CardTitle className="flex items-center gap-2 text-amber-900">
                    <Download className="w-5 h-5" />
                    تصدير البيانات
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 text-center">
                  <p className="text-gray-600 mb-4">تصدير جميع البيانات إلى ملف Excel</p>
                  <Button 
                    onClick={handleExportData}
                    className="bg-green-600 hover:bg-green-700 w-full"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    تصدير إلى Excel
                  </Button>
                </CardContent>
              </Card>

              {/* Import Data */}
              <Card className="shadow-lg border-amber-200">
                <CardHeader className="bg-amber-100">
                  <CardTitle className="flex items-center gap-2 text-amber-900">
                    <Upload className="w-5 h-5" />
                    استيراد البيانات
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 text-center">
                  <p className="text-gray-600 mb-4">استيراد البيانات من ملف Excel</p>
                  <Button 
                    onClick={() => setIsImportDialogOpen(true)}
                    className="bg-blue-600 hover:bg-blue-700 w-full"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {t('importFromExcel')}
                  </Button>
                </CardContent>
              </Card>

              {/* Clear All Data */}
              <Card className="shadow-lg border-red-200">
                <CardHeader className="bg-red-100">
                  <CardTitle className="flex items-center gap-2 text-red-900">
                    <AlertTriangle className="w-5 h-5" />
                    حذف جميع البيانات
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 text-center">
                  <p className="text-gray-600 mb-4">{t('confirmDelete')}</p>
                  <Button 
                    onClick={() => executeWithPassword(
                      handleClearAllData,
                      t('clearAllData'),
                      "هذا الإجراء سيحذف جميع البيانات نهائياً ولا يمكن التراجع عنه!"
                    )}
                    variant="destructive"
                    className="w-full"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    حذف جميع البيانات
                  </Button>
                </CardContent>
              </Card>

              {/* Change Password */}
              <Card className="shadow-lg border-amber-200">
                <CardHeader className="bg-amber-100">
                  <CardTitle className="flex items-center gap-2 text-amber-900">
                    <Settings className="w-5 h-5" />
                    {t('changePassword')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 text-center">
                  <p className="text-gray-600 mb-4">تغيير كلمة مرور المدير</p>
                  <Button 
                    onClick={() => setIsChangePasswordDialogOpen(true)}
                    className="bg-purple-600 hover:bg-purple-700 w-full"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    {t('changePassword')}
                  </Button>
                </CardContent>
              </Card>

              {/* Church Settings */}
              <Card className="shadow-lg border-amber-200">
                <CardHeader className="bg-amber-100">
                  <CardTitle className="flex items-center gap-2 text-amber-900">
                    <Settings className="w-5 h-5" />
                    إعدادات الكنيسة
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 text-center">
                  <p className="text-gray-600 mb-4">تحديث معلومات الكنيسة</p>
                  <Button 
                    onClick={openChurchSettingsDialog}
                    className="bg-indigo-600 hover:bg-indigo-700 w-full"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    إعدادات الكنيسة
                  </Button>
                </CardContent>
              </Card>

              {/* iPhone Install Guide */}
              <Card className="shadow-lg border-blue-200">
                <CardHeader className="bg-blue-100">
                  <CardTitle className="flex items-center gap-2 text-blue-900">
                    📱 تثبيت على iPhone
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 text-center">
                  <p className="text-gray-600 mb-4">دليل تثبيت التطبيق على الآيفون</p>
                  <Button 
                    onClick={() => window.open('/install-guide-ar.html', '_blank')}
                    className="bg-blue-600 hover:bg-blue-700 w-full"
                  >
                    📖 دليل التثبيت
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* How To Use Tab */}
          <TabsContent value="how-to-use">
            <Card className="shadow-lg border-blue-200">
              <CardHeader className="bg-gradient-to-r from-blue-100 to-indigo-100">
                <CardTitle className="flex items-center gap-2 text-blue-900 text-center justify-center">
                  <BookOpen className="w-6 h-6" />
                  {language === 'de' ? 'Benutzerhandbuch' : 'طريقة الاستخدام'}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-6">
                  
                  {/* Section 1: Dashboard */}
                  <div className="bg-amber-50 rounded-xl p-5 border border-amber-200">
                    <h3 className="font-bold text-amber-900 text-lg mb-3 flex items-center gap-2">
                      <BarChart3 className="w-5 h-5" />
                      {language === 'de' ? '1. Übersicht (Dashboard)' : '1. لوحة المعلومات (الرئيسية)'}
                    </h3>
                    <ul className="space-y-2 text-gray-700 text-sm">
                      <li>• {language === 'de' ? 'Zeigt die Gesamtzahl der Diakone und ihre Teilnahme' : 'تعرض إجمالي عدد الشمامسة ونسبة مشاركتهم'}</li>
                      <li>• {language === 'de' ? 'Statistiken über Lesungen und Dienste' : 'إحصائيات عن القراءات والخدمات'}</li>
                      <li>• {language === 'de' ? 'Diakone, die lange nicht gedient haben' : 'الشمامسة الذين لم يخدموا منذ فترة طويلة'}</li>
                      <li>• {language === 'de' ? 'Koptischer und gregorianischer Kalender' : 'التقويم القبطي والميلادي'}</li>
                    </ul>
                  </div>

                  {/* Section 2: New Assignment */}
                  <div className="bg-green-50 rounded-xl p-5 border border-green-200">
                    <h3 className="font-bold text-green-900 text-lg mb-3 flex items-center gap-2">
                      <Plus className="w-5 h-5" />
                      {language === 'de' ? '2. Neue Zuweisung' : '2. توزيع جديد'}
                    </h3>
                    <ul className="space-y-2 text-gray-700 text-sm">
                      <li>• {language === 'de' ? 'Wählen Sie einen Diakon aus der Liste' : 'اختر شماس من القائمة'}</li>
                      <li>• {language === 'de' ? 'Wählen Sie die Art der Lesung oder des Dienstes' : 'اختر نوع القراءة أو الخدمة'}</li>
                      <li>• {language === 'de' ? 'Wählen Sie das Datum und speichern Sie' : 'اختر التاريخ واحفظ'}</li>
                      <li>• {language === 'de' ? 'Sie können mehrere Zuweisungen für denselben Tag hinzufügen' : 'يمكنك إضافة عدة توزيعات لنفس اليوم'}</li>
                    </ul>
                  </div>

                  {/* Section 3: Deacons List */}
                  <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
                    <h3 className="font-bold text-blue-900 text-lg mb-3 flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      {language === 'de' ? '3. Diakonenliste' : '3. قائمة الشمامسة'}
                    </h3>
                    <ul className="space-y-2 text-gray-700 text-sm">
                      <li>• {language === 'de' ? 'Alle registrierten Diakone anzeigen' : 'عرض جميع الشمامسة المسجلين'}</li>
                      <li>• {language === 'de' ? 'Suche nach Name' : 'البحث بالاسم'}</li>
                      <li>• {language === 'de' ? 'Diakon hinzufügen/bearbeiten/löschen' : 'إضافة/تعديل/حذف شماس'}</li>
                      <li>• {language === 'de' ? 'Statistiken für jeden Diakon anzeigen' : 'عرض إحصائيات كل شماس'}</li>
                    </ul>
                  </div>

                  {/* Section 4: Assignments */}
                  <div className="bg-purple-50 rounded-xl p-5 border border-purple-200">
                    <h3 className="font-bold text-purple-900 text-lg mb-3 flex items-center gap-2">
                      <CalendarIcon className="w-5 h-5" />
                      {language === 'de' ? '4. Zuweisungen' : '4. التوزيعات'}
                    </h3>
                    <ul className="space-y-2 text-gray-700 text-sm">
                      <li>• {language === 'de' ? 'Alle Zuweisungen nach Datum anzeigen' : 'عرض جميع التوزيعات حسب التاريخ'}</li>
                      <li>• {language === 'de' ? 'Filtern nach Diensttyp' : 'تصفية حسب نوع الخدمة'}</li>
                      <li>• {language === 'de' ? 'Zuweisung bearbeiten oder löschen' : 'تعديل أو حذف توزيع'}</li>
                      <li>• {language === 'de' ? 'Drucken oder als Excel exportieren' : 'طباعة أو تصدير كملف Excel'}</li>
                    </ul>
                  </div>

                  {/* Section 5: Bulk Attendance */}
                  <div className="bg-orange-50 rounded-xl p-5 border border-orange-200">
                    <h3 className="font-bold text-orange-900 text-lg mb-3 flex items-center gap-2">
                      <CheckSquare className="w-5 h-5" />
                      {language === 'de' ? '5. Sammelanwesenheit' : '5. تسجيل الحضور الجماعي'}
                    </h3>
                    <ul className="space-y-2 text-gray-700 text-sm">
                      <li>• {language === 'de' ? 'Schnelle Anwesenheitserfassung für mehrere Diakone' : 'تسجيل حضور سريع لعدة شمامسة'}</li>
                      <li>• {language === 'de' ? 'Alle auswählen oder einzeln markieren' : 'تحديد الكل أو اختيار فردي'}</li>
                      <li>• {language === 'de' ? 'Geeignet für Sonntagsgottesdienste' : 'مناسب لقداسات الأحد'}</li>
                    </ul>
                  </div>

                  {/* Section 6: Reports */}
                  <div className="bg-teal-50 rounded-xl p-5 border border-teal-200">
                    <h3 className="font-bold text-teal-900 text-lg mb-3 flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      {language === 'de' ? '6. Berichte' : '6. التقارير'}
                    </h3>
                    <ul className="space-y-2 text-gray-700 text-sm">
                      <li>• {language === 'de' ? 'Detaillierte Statistiken für jeden Diakon' : 'إحصائيات تفصيلية لكل شماس'}</li>
                      <li>• {language === 'de' ? 'Monatliche und jährliche Berichte' : 'تقارير شهرية وسنوية'}</li>
                      <li>• {language === 'de' ? 'Export als Excel-Datei' : 'تصدير كملف Excel'}</li>
                    </ul>
                  </div>

                  {/* Section 7: Data Management */}
                  <div className="bg-red-50 rounded-xl p-5 border border-red-200">
                    <h3 className="font-bold text-red-900 text-lg mb-3 flex items-center gap-2">
                      <Database className="w-5 h-5" />
                      {language === 'de' ? '7. Datenverwaltung' : '7. إدارة البيانات'}
                    </h3>
                    <ul className="space-y-2 text-gray-700 text-sm">
                      <li>• {language === 'de' ? 'Backup erstellen und wiederherstellen' : 'إنشاء نسخة احتياطية واستعادتها'}</li>
                      <li>• {language === 'de' ? 'Diakone aus Excel importieren' : 'استيراد الشمامسة من ملف Excel'}</li>
                      <li>• {language === 'de' ? 'Alle Daten löschen (mit Passwort)' : 'مسح جميع البيانات (بكلمة المرور)'}</li>
                      <li>• {language === 'de' ? 'Kontoeinstellungen ändern' : 'تغيير إعدادات الحساب'}</li>
                    </ul>
                  </div>

                  {/* Tips */}
                  <div className="bg-yellow-50 rounded-xl p-5 border border-yellow-300">
                    <h3 className="font-bold text-yellow-800 text-lg mb-3 flex items-center gap-2">
                      💡 {language === 'de' ? 'Nützliche Tipps' : 'نصائح مفيدة'}
                    </h3>
                    <ul className="space-y-2 text-gray-700 text-sm">
                      <li>• {language === 'de' ? 'Verwenden Sie die Suchfunktion für schnellen Zugriff' : 'استخدم خاصية البحث للوصول السريع'}</li>
                      <li>• {language === 'de' ? 'Erstellen Sie regelmäßig Backups' : 'قم بعمل نسخة احتياطية بشكل دوري'}</li>
                      <li>• {language === 'de' ? 'Überprüfen Sie die Statistiken wöchentlich' : 'راجع الإحصائيات أسبوعياً'}</li>
                      <li>• {language === 'de' ? 'Ändern Sie Ihr Passwort regelmäßig' : 'غيّر كلمة المرور بشكل دوري'}</li>
                    </ul>
                  </div>

                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contact Us Tab */}
          <TabsContent value="contact-us">
            <Card className="shadow-lg border-amber-200">
              <CardHeader className="bg-gradient-to-r from-amber-100 to-orange-100">
                <CardTitle className="flex items-center gap-2 text-amber-900 text-center justify-center">
                  <MessageCircle className="w-6 h-6" />
                  {language === 'de' ? 'Kontaktieren Sie uns' : 'تواصل معنا'}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {/* Blessings Text */}
                <div className="text-center mb-8">
                  <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
                    <div className="flex justify-center gap-3 mb-4">
                      <img 
                        src="/diocese-logo.png" 
                        alt="Diocese Logo" 
                        className="w-12 h-12 rounded-full border-2 border-amber-300"
                      />
                      <img 
                        src="/icon-192.png" 
                        alt="Church Logo" 
                        className="w-12 h-12 rounded-full border-2 border-amber-300"
                      />
                    </div>
                    
                    {language === 'de' ? (
                      <div className="space-y-2 text-gray-700">
                        <p className="font-semibold text-amber-900">Mit den Gebeten Seiner Heiligkeit Papst Tawadros II.</p>
                        <p className="text-sm">Papst von Alexandrien und Patriarch des Stuhls des Heiligen Markus</p>
                        <div className="border-t border-amber-200 my-3"></div>
                        <p className="font-semibold text-purple-800">und unter der Schirmherrschaft Seiner Gnaden Bischof Anba Deuscoros</p>
                        <p className="text-sm">Bischof von Süddeutschland und Abt des Klosters des großen heiligen</p>
                        <p className="text-sm">Anba Antonius in Kröffelbach</p>
                      </div>
                    ) : (
                      <div className="space-y-2 text-gray-700">
                        <p className="font-semibold text-amber-900">بصلوات قداسة البابا المعظَّم الأنبا تواضروس الثاني</p>
                        <p className="text-sm">بابا الإسكندرية وبطريرك الكرازة المرقسية</p>
                        <div className="border-t border-amber-200 my-3"></div>
                        <p className="font-semibold text-purple-800">وتحت رعاية نيافة الحبر الجليل الأنبا ديوسقوروس</p>
                        <p className="text-sm">أسقف جنوب ألمانيا ورئيس دير القديس العظيم</p>
                        <p className="text-sm">الأنبا أنطونيوس بكريفلباخ</p>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Contact Form */}
                <div className="mb-6">
                  <ContactForm language={language} API={API} />
                </div>
                
                {/* WhatsApp Button */}
                <div className="text-center">
                  <a 
                    href="https://wa.me/4917613890009" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors shadow-lg"
                  >
                    <MessageCircle className="w-5 h-5" />
                    {language === 'de' ? 'Über WhatsApp kontaktieren' : 'تواصل عبر واتساب'}
                  </a>
                  <p className="text-gray-500 text-sm mt-2">+49 176 1389 0009</p>
                </div>
                
                {/* Copyright */}
                <div className="text-center mt-6 pt-4 border-t border-amber-200">
                  <p className="text-sm text-gray-500">
                    {t('copyright')}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {language === 'de' ? 'Diakonenverwaltungssystem' : 'نظام إدارة خدمات الشموسية'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Add Deacon Dialog */}
          <Dialog open={isAddDeaconOpen} onOpenChange={setIsAddDeaconOpen}>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>{t('addDeacon')}</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <Input
                  placeholder={`${t('deaconName')} *`}
                  value={newDeaconName}
                  onChange={(e) => setNewDeaconName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddDeacon()}
                />
                <Input
                  placeholder="رقم التليفون (اختياري)"
                  value={newDeaconPhone}
                  onChange={(e) => setNewDeaconPhone(e.target.value)}
                />
                <Input
                  placeholder={`${t('address')} (${t('optional')})`}
                  value={newDeaconAddress}
                  onChange={(e) => setNewDeaconAddress(e.target.value)}
                />
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => {
                    setIsAddDeaconOpen(false);
                    setNewDeaconName("");
                    setNewDeaconPhone("");
                    setNewDeaconAddress("");
                  }}>
                    إلغاء
                  </Button>
                  <Button onClick={handleAddDeacon} className="bg-amber-600 hover:bg-amber-700">
                    إضافة
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Edit Deacon Dialog */}
          <Dialog open={isEditDeaconDialogOpen} onOpenChange={setIsEditDeaconDialogOpen}>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>{t('editDeacon')}</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <Input
                  placeholder={`${t('deaconName')} *`}
                  value={editingDeacon?.newName || ""}
                  onChange={(e) => setEditingDeacon(prev => ({ ...prev, newName: e.target.value }))}
                />
                <Input
                  placeholder="رقم التليفون (اختياري)"
                  value={editingDeacon?.newPhone || ""}
                  onChange={(e) => setEditingDeacon(prev => ({ ...prev, newPhone: e.target.value }))}
                />
                <Input
                  placeholder={`${t('address')} (${t('optional')})`}
                  value={editingDeacon?.newAddress || ""}
                  onChange={(e) => setEditingDeacon(prev => ({ ...prev, newAddress: e.target.value }))}
                />
                <Input
                  type="password"
                  placeholder={t('password')}
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                />
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setIsEditDeaconDialogOpen(false)}>
                    إلغاء
                  </Button>
                  <Button 
                    onClick={async () => {
                      try {
                        await handleEditDeacon(
                          editingDeacon.id, 
                          editingDeacon.newName, 
                          editingDeacon.newPhone, 
                          editingDeacon.newAddress, 
                          adminPassword
                        );
                        setAdminPassword("");
                      } catch (error) {
                        // Error already handled in handleEditDeacon
                      }
                    }}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    تعديل
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Ranks Management Dialog */}
          <Dialog open={isRanksDialogOpen} onOpenChange={setIsRanksDialogOpen}>
            <DialogContent className="sm:max-w-[450px]">
              <DialogHeader>
                <DialogTitle>إدارة الرتب الشموسية</DialogTitle>
              </DialogHeader>
              <div className="grid gap-3 py-4">
                {editableRanks.map((rank, index) => (
                  <div key={rank.id} className="flex items-center gap-2">
                    <span className="text-sm font-medium text-amber-700 w-6">{index + 1}.</span>
                    <Input
                      data-testid={`rank-name-input-${index}`}
                      value={rank.name}
                      onChange={(e) => {
                        const updated = [...editableRanks];
                        updated[index] = {...rank, name: e.target.value};
                        setEditableRanks(updated);
                      }}
                      className="flex-1"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditableRanks(editableRanks.filter((_, i) => i !== index))}
                      className="border-red-300 text-red-600 hover:bg-red-50 px-2"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  onClick={() => setEditableRanks([...editableRanks, {id: Date.now().toString(), name: "", order: editableRanks.length + 1}])}
                  className="border-dashed border-amber-300 text-amber-700"
                  data-testid="add-rank-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> إضافة رتبة
                </Button>
                <div className="flex gap-2 justify-end mt-2">
                  <Button variant="outline" onClick={() => setIsRanksDialogOpen(false)}>إلغاء</Button>
                  <Button onClick={handleSaveRanks} className="bg-amber-600 hover:bg-amber-700" data-testid="save-ranks-btn">حفظ</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Edit Assignment Dialog */}
          <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>تعديل التوزيع</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div>
                  <p className="text-sm text-gray-600 mb-2">نوع الخدمة: {editingAssignment?.reading_type}</p>
                  <Select 
                    value={editingAssignment?.newDeaconId || editingAssignment?.deacon_id || ""} 
                    onValueChange={(value) => setEditingAssignment(prev => ({ ...prev, newDeaconId: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('selectDeacon')} />
                    </SelectTrigger>
                    <SelectContent>
                      {deacons && deacons.map((deacon) => (
                        <SelectItem key={deacon.id} value={deacon.id}>
                          {deacon.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Input
                  type="password"
                  placeholder={t('password')}
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                />
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                    إلغاء
                  </Button>
                  <Button 
                    onClick={async () => {
                      try {
                        await handleEditAssignment(
                          editingAssignment.id, 
                          editingAssignment.newDeaconId || editingAssignment.deacon_id, 
                          adminPassword
                        );
                        setAdminPassword("");
                      } catch (error) {
                        if (error.response?.status === 401) {
                          toast.error(t('wrongPassword'));
                        } else {
                          toast.error(t('errorEditingAssignment'));
                        }
                      }
                    }}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    تعديل
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Password Dialog */}
          <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>{passwordDialogTitle}</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <p className="text-gray-600">{passwordDialogMessage}</p>
                <Input
                  type="password"
                  placeholder={t('password')}
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                />
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setIsPasswordDialogOpen(false)}>
                    إلغاء
                  </Button>
                  <Button onClick={handlePasswordSubmit} className="bg-amber-600 hover:bg-amber-700">
                    تأكيد
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Change Password Dialog */}
          <Dialog open={isChangePasswordDialogOpen} onOpenChange={setIsChangePasswordDialogOpen}>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>{t('changePassword')}</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <Input
                  type="password"
                  placeholder={t('oldPassword')}
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                />
                <Input
                  type="password"
                  placeholder={t('newPassword')}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setIsChangePasswordDialogOpen(false)}>
                    إلغاء
                  </Button>
                  <Button onClick={handleChangePassword} className="bg-amber-600 hover:bg-amber-700">
                    تغيير
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Import Data Dialog */}
          <Dialog open={isImportDialogOpen} onOpenChange={(open) => {
            setIsImportDialogOpen(open);
            if (!open) setImportType("assignments");
          }}>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>استيراد البيانات</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                {/* Import Type Selection */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">نوع الاستيراد:</label>
                  <div className="flex gap-3">
                    <Button
                      variant={importType === "deacons" ? "default" : "outline"}
                      onClick={() => setImportType("deacons")}
                      className="flex-1"
                    >
                      استيراد الشمامسة فقط
                    </Button>
                    <Button
                      variant={importType === "assignments" ? "default" : "outline"}
                      onClick={() => setImportType("assignments")}
                      className="flex-1"
                    >
                      {t('importAssignments')}
                    </Button>
                  </div>
                </div>

                {/* Instructions based on import type */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                  {importType === "deacons" ? (
                    <div>
                      <p className="font-medium text-blue-800 mb-1">📋 لاستيراد الشمامسة:</p>
                      <p className="text-blue-700">يجب أن يحتوي الملف على عمود باسم:</p>
                      <ul className="list-disc list-inside text-blue-600 mt-1">
                        <li><strong>اسم الشماس</strong> أو <strong>الاسم</strong> (مطلوب)</li>
                        <li>الهاتف (اختياري)</li>
                        <li>العنوان (اختياري)</li>
                      </ul>
                      <Button
                        variant="link"
                        className="mt-2 text-blue-700 p-0 h-auto"
                        onClick={() => {
                          window.open(`${API}/deacons/template/download`, '_blank');
                        }}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        تحميل قالب Excel جاهز
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <p className="font-medium text-blue-800 mb-1">📋 لاستيراد التوزيعات:</p>
                      <p className="text-blue-700">يجب أن يحتوي الملف على الأعمدة:</p>
                      <ul className="list-disc list-inside text-blue-600 mt-1">
                        <li>التاريخ</li>
                        <li>اسم الشماس</li>
                        <li>نوع القراءة</li>
                      </ul>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">اختر ملف Excel:</label>
                  <Input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => {
                      if (importType === "deacons") {
                        handleImportDeacons(e.target.files?.[0]);
                      } else {
                        handleImportData(e.target.files?.[0]);
                      }
                    }}
                    disabled={isImporting}
                  />
                </div>
                
                {isImporting && (
                  <div className="flex items-center gap-2 text-blue-600">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                    جاري الاستيراد...
                  </div>
                )}
                
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setIsImportDialogOpen(false)}>
                    إلغاء
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Church Settings Dialog */}
          <Dialog open={isChurchSettingsDialogOpen} onOpenChange={setIsChurchSettingsDialogOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  إعدادات الكنيسة
                </DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">اسم الكنيسة كاملاً</label>
                  <Input
                    value={churchSettings.church_name}
                    onChange={(e) => setChurchSettings(prev => ({ ...prev, church_name: e.target.value }))}
                    placeholder="اسم الكنيسة الكامل"
                    className="text-right"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">اسم الكنيسة مختصر</label>
                  <Input
                    value={churchSettings.church_name_short}
                    onChange={(e) => setChurchSettings(prev => ({ ...prev, church_name_short: e.target.value }))}
                    placeholder={t('churchNameShort')}
                    className="text-right"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">الموقع/المدينة</label>
                  <Input
                    value={churchSettings.church_location}
                    onChange={(e) => setChurchSettings(prev => ({ ...prev, church_location: e.target.value }))}
                    placeholder="موقع أو مدينة الكنيسة"
                    className="text-right"
                  />
                </div>
                
                {churchInfo && churchInfo.source === 'database' && (
                  <div className="text-xs text-green-600 bg-green-50 p-2 rounded">
                    ✅ الإعدادات محفوظة في قاعدة البيانات
                  </div>
                )}
                
                {churchInfo && churchInfo.source === 'environment' && (
                  <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                    ℹ️ الإعدادات الحالية من ملف التكوين
                  </div>
                )}
              </div>
              
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setIsChurchSettingsDialogOpen(false)}>
                  إلغاء
                </Button>
                <Button 
                  onClick={() => {
                    setPasswordDialogAction(() => handleUpdateChurchSettings);
                    setPasswordDialogTitle("تحديث إعدادات الكنيسة");
                    setPasswordDialogMessage("أدخل كلمة مرور المدير لتحديث معلومات الكنيسة:");
                    setIsPasswordDialogOpen(true);
                  }}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  حفظ التغييرات
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Service Type Add Dialog */}
          <Dialog open={isServiceTypeDialogOpen} onOpenChange={setIsServiceTypeDialogOpen}>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>إضافة نوع خدمة جديد</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <Input
                  placeholder="اسم نوع الخدمة"
                  value={newServiceType.name}
                  onChange={(e) => setNewServiceType(prev => ({ ...prev, name: e.target.value }))}
                />
                <Input
                  placeholder="وصف (اختياري)"
                  value={newServiceType.description}
                  onChange={(e) => setNewServiceType(prev => ({ ...prev, description: e.target.value }))}
                />
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => {
                    setIsServiceTypeDialogOpen(false);
                    setNewServiceType({ name: "", description: "" });
                  }}>
                    إلغاء
                  </Button>
                  <Button 
                    onClick={() => {
                      executeWithPassword(
                        handleAddServiceType,
                        t('addServiceType'),
                        "أدخل كلمة المرور لإضافة نوع خدمة جديد"
                      );
                    }}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    إضافة
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Service Type Edit Dialog */}
          <Dialog open={isEditServiceTypeDialogOpen} onOpenChange={setIsEditServiceTypeDialogOpen}>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>تعديل نوع الخدمة</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <Input
                  placeholder="اسم نوع الخدمة"
                  value={editingServiceType?.name || ""}
                  onChange={(e) => setEditingServiceType(prev => prev ? { ...prev, name: e.target.value } : null)}
                />
                <Input
                  placeholder="وصف (اختياري)"
                  value={editingServiceType?.description || ""}
                  onChange={(e) => setEditingServiceType(prev => prev ? { ...prev, description: e.target.value } : null)}
                />
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => {
                    setIsEditServiceTypeDialogOpen(false);
                    setEditingServiceType(null);
                  }}>
                    إلغاء
                  </Button>
                  <Button 
                    onClick={() => {
                      executeWithPassword(
                        handleEditServiceType,
                        t('editServiceType'),
                        `تعديل نوع الخدمة "${editingServiceType?.name}"`
                      );
                      setIsEditServiceTypeDialogOpen(false);
                    }}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    تعديل
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Reading Details Dialog */}
          <Dialog open={isReadingDetailsDialogOpen} onOpenChange={setIsReadingDetailsDialogOpen}>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle>تفاصيل قراءات {selectedDeaconStats?.name}</DialogTitle>
              </DialogHeader>
              <div className="py-4">
                {selectedDeaconStats && (
                  <div className="space-y-4">
                    <div className="bg-amber-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-amber-900 mb-2">
                        إجمالي القراءات: <span className="number-display">{selectedDeaconStats.total_readings}</span>
                      </h3>
                    </div>
                    <div className="grid gap-3">
                      {Object.entries(selectedDeaconStats.readings_by_type || {}).map(([type, count]) => {
                        const percentage = selectedDeaconStats.total_readings > 0 
                          ? (count / selectedDeaconStats.total_readings * 100).toFixed(1)
                          : 0;
                        
                        return (
                          <div key={type} className="bg-white p-3 rounded border border-gray-200">
                            <div className="flex justify-between items-center mb-2">
                              <span className="font-medium text-gray-900">{type}</span>
                              <Badge className="bg-amber-100 text-amber-800">
                                <span className="number-display">{count}</span> ({percentage}%)
                              </Badge>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-amber-500 h-2 rounded-full transition-all duration-300" 
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className="flex justify-end mt-6">
                  <Button onClick={() => setIsReadingDetailsDialogOpen(false)}>
                    إغلاق
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
            </div>
          </div>
        </Tabs>
      </div>
      
      {/* Last Updated - Moved to bottom */}
      {lastDataLoad && (
        <div className="text-center py-4 bg-gray-50">
          <div className="text-xs text-gray-500">
            آخر تحديث: {lastDataLoad}
          </div>
        </div>
      )}
      
      <Toaster />
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

function AppContent() {
  const { isAuthenticated, isBishop, loading, user, token, logout, impersonating, impersonatedChurch, startImpersonation, exitImpersonation } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-amber-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // If user is bishop (and not impersonating), show bishop dashboard
  if (isBishop()) {
    return (
      <BishopDashboard 
        user={user} 
        token={token} 
        onLogout={logout}
        onImpersonate={startImpersonation}
      />
    );
  }

  return (
    <div className="App" dir="rtl">
      {/* Show impersonation banner if applicable */}
      {impersonating && impersonatedChurch && (
        <div className="bg-indigo-600 text-white px-4 py-2 flex items-center justify-between">
          <span className="text-sm">
            ⚡ أنت تتصفح كمدير لكنيسة: <strong>{impersonatedChurch.name}</strong>
          </span>
          <button 
            onClick={exitImpersonation}
            className="bg-white text-indigo-600 px-3 py-1 rounded text-sm font-medium hover:bg-indigo-50 transition-colors"
          >
            العودة للوحة الأسقف
          </button>
        </div>
      )}
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Dashboard />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;